import React, { useEffect, useState, useCallback, useMemo, Suspense } from "react";
import { gymSlugFromHost } from "./utils/gymHost";
// Statically imported, unlike every other page here: a React.lazy chunk has to be
// downloaded on demand, and this screen only ever renders when there is no network
// to download it with — it would sit on the Suspense fallback forever. A few KB in
// the main bundle is the price of the offline page actually appearing offline.
import Offline from "./pages/Offline.jsx";
import PageSkeleton from "./components/ui/PageSkeleton";
import { useDispatch, useSelector } from "react-redux";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import "./App.css";
import "./styles/common-styles.css";
import "./styles/filters-styles.css";
import "./styles/centralised-plan/layouts.css";
import "./styles/wtf-payouts/wtf-payouts.css";
import "./styles/wtf-payouts/settlement-systems.css";
import "./styles/wtf-payouts/profit-share.css";
import "./styles/campaign/campaign.css";
import "./styles/incentive-system/incentive-systems.css";
import "./styles/payment-link/payment-link.css";
import "./styles/payment-link/payment-link.css";
import "./styles/pt/personal-training.css";
import "./styles/customize-components/date-ranger.css";
import "./styles/customize-components/global-search.css";
// Mounted below as a sibling of the router — NOT inside any auth wrapper. The
// consent gate fails closed, so if this never renders GTM never loads and all
// analytics silently stop. Ad traffic lands on public/subdomain routes, so it
// has to be outside the authenticated tree.
import ConsentManager from "./components/consent/ConsentManager.jsx";
import PrivateRoute from "./components/auth/PrivateRoute.jsx";
import Layout from "./components/Layout.jsx";
import SuspenseWrapper from "./components/SuspenseWrapper.jsx";
// The four payroll-family pages that were commented out on 07-13 are back, and
// they are now LAZY (declared with the other owner-portal pages below) rather
// than the eager imports they used to be. OwnerAttendance pulls in
// react-apexcharts; eager-importing it would put the whole charting library in
// the main bundle for every owner, including the ones who never open the page.
// import OwnerSetPayouts from "./pages/owner-dashboard/OwnerSetPayouts.jsx";   // still removed from SaaS (07-13)
import OwnerRoute from "./components/auth/OwnerRoute.jsx";
import SaasAdminRoute from "./components/auth/SaasAdminRoute.jsx";
// import { ACCOUNT_TYPES } from "./data/Constants.js";
import { selectIsAuth, selectToken } from "./components/auth/auth.selector.js";
import { logout } from "./redux/reducers/authSlice.js";
import ComposeVC from "./components/automation/voice-campaigns/ComposeVC.jsx";
import PersonalTraining from "./components/pt/PersonalTraining.jsx";
import SubscriptionDetails from "./components/subs/SubscriptionDetails.jsx";
import { CurrencyProvider } from "./context-v2/CurrencyContext.jsx";
import WTFPayouts from "./components/payouts-wtf/WTFPayouts.jsx";
import SettlementSystem from "./components/payouts-wtf/settlement-cto/SettlementSystem.jsx";
import IncentiveSystem from "./components/incentive-system/IncentiveSystem.jsx";
import Plans from "./components/centralised-plan-system/Plans.jsx";
import Login from "./components/auth/Login.jsx";
import OpenPages from "./components/Open-pages/OpenPages.jsx";
import KeywordActions from "./components/automation/keyword/KeywordActions";
import { isTokenExpired, isSessionExpiredResponse, isSupportEndedResponse, isAccessRefusalResponse } from "./utils/token.js";
import { isSupportMode, markSupportEnded } from "./utils/supportMode";
import { toast } from "react-toastify";
import PTFlow from "./components/pt/pt-flow-new/PTFlow.jsx";
import TvNotification from "./components/tv/tvNotification.jsx";
// import LayoutHome from "./pages/LayoutHome.jsx";
// import TimeSheet from "./pages/time-sheet/TimeSheet.jsx";
// import AcademyCourseDetail from "./components/academy/academy-course-detail/AcademyCourseDetail.jsx";
// import CourseList from "./components/academy/academy-courses/CourseList.jsx";
import AcademyCourseSchedule from "./components/academy/academy-course-schedule/AcademyCourseSchedule.jsx";
// import CategoryList from "./components/academy/academy-course-categories/CategoryList.jsx";
// import LeadReports from "./pages/lead-management/LeadReports.jsx";
import TicketUserRoles from "./components/ticket-system/ticket-user-roles/TicketUserRoles.jsx";
import TicketSystemManagement from "./components/ticket-system/ticket-management/TicketSystemManagement.jsx";
import SLARules from "./components/ticket-system/ticket-SLA/SLARules.jsx";
// import RoasterManager from "./pages/payrole-system/RoasterManager.jsx";
// import LiveCheckin from "./pages/live-monitoring/LiveCheckin.jsx";
import ChatHistory from "./components/chat-history/ChatHistory.jsx";


// Email Broadcast System
import Home from "./pages/home/Home.jsx";

// Declared after the imports, not among them: eslint `import/first` is an ERROR
// in CI (warnings are errors there) and failed the production build even though
// the local build passed.
const GymHostLanding = React.lazy(() => import("./components/tenant/GymHostLanding"));

// ---- lazy-loaded route pages (code-split; see perf audit) ----
const OwnerSignupWizard = React.lazy(() => import("./pages/onboarding/OwnerSignupWizard.jsx"));
const OwnerHome = React.lazy(() => import("./pages/owner-dashboard/OwnerHome.jsx"));
const OwnerPortalLayout = React.lazy(() => import("./pages/owner-dashboard/OwnerPortalLayout.jsx"));
const ManageGyms = React.lazy(() => import("./pages/owner-dashboard/ManageGyms.jsx"));
const ManagePlans = React.lazy(() => import("./pages/owner-dashboard/ManagePlans.jsx"));
const AddGymWizard = React.lazy(() => import("./pages/owner-dashboard/AddGymWizard.jsx"));
const OwnerPtPlans = React.lazy(() => import("./pages/owner-dashboard/OwnerPtPlans.jsx"));
const OwnerPtPlanForm = React.lazy(() => import("./pages/owner-dashboard/OwnerPtPlanForm.jsx"));
const OwnerPlanForm = React.lazy(() => import("./pages/owner-dashboard/OwnerPlanForm.jsx"));
const OwnerStaff = React.lazy(() => import("./pages/owner-dashboard/OwnerStaff.jsx"));
const OwnerStaffForm = React.lazy(() => import("./pages/owner-dashboard/OwnerStaffForm.jsx"));
const OwnerStaffOffboard = React.lazy(() => import("./pages/owner-dashboard/OwnerStaffOffboard.jsx"));
// Roles & Permissions — one screen replaces Access Groups, Access Assignment, the
// Permission Matrix and the read-only Roles page (their URLs redirect to its tabs).
const OwnerRolesPermissions = React.lazy(() => import("./pages/owner-dashboard/OwnerRolesPermissions.jsx"));
const OwnerMembers = React.lazy(() => import("./pages/owner-dashboard/OwnerMembers.jsx"));
const OwnerMemberForm = React.lazy(() => import("./pages/owner-dashboard/OwnerMemberForm.jsx"));
const OwnerSubscriptions = React.lazy(() => import("./pages/owner-dashboard/OwnerSubscriptions.jsx"));
const OwnerRenewalsHub = React.lazy(() => import("./pages/owner-dashboard/OwnerRenewalsHub.jsx"));
const OwnerLeads = React.lazy(() => import("./pages/owner-dashboard/OwnerLeads.jsx"));
const OwnerHabits = React.lazy(() => import("./pages/owner-dashboard/OwnerHabits.jsx"));
const OwnerProgressPhotos = React.lazy(() => import("./pages/owner-dashboard/OwnerProgressPhotos.jsx"));
const OwnerWallet = React.lazy(() => import("./pages/owner-dashboard/OwnerWallet.jsx"));
const OwnerRepMax = React.lazy(() => import("./pages/owner-dashboard/OwnerRepMax.jsx"));
const OwnerShifts = React.lazy(() => import("./pages/owner-dashboard/OwnerShifts.jsx"));
const OwnerAttendance = React.lazy(() => import("./pages/owner-dashboard/OwnerAttendance.jsx"));
const OwnerLeave = React.lazy(() => import("./pages/owner-dashboard/OwnerLeave.jsx"));
const OwnerIncentive = React.lazy(() => import("./pages/owner-dashboard/OwnerIncentive.jsx"));
const OwnerPayroll = React.lazy(() => import("./pages/owner-dashboard/OwnerPayroll.jsx"));
const OwnerChallenges = React.lazy(() => import("./pages/owner-dashboard/OwnerChallenges.jsx"));
const OwnerAutomations = React.lazy(() => import("./pages/owner-dashboard/OwnerAutomations.jsx"));
const OwnerCoupons = React.lazy(() => import("./pages/owner-dashboard/OwnerCoupons.jsx"));
const OwnerClasses = React.lazy(() => import("./pages/owner-dashboard/OwnerClasses.jsx"));
const OwnerTimetable = React.lazy(() => import("./pages/owner-dashboard/OwnerTimetable.jsx"));
const OwnerCampaigns = React.lazy(() => import("./pages/owner-dashboard/OwnerCampaigns.jsx"));
const OwnerSegments = React.lazy(() => import("./pages/owner-dashboard/OwnerSegments.jsx"));
const OwnerTemplates = React.lazy(() => import("./pages/owner-dashboard/OwnerTemplates.jsx"));
const OwnerNutrition = React.lazy(() => import("./pages/owner-dashboard/OwnerNutrition.jsx"));
const OwnerAssessments = React.lazy(() => import("./pages/owner-dashboard/OwnerAssessments.jsx"));
const OwnerWorkoutPrograms = React.lazy(() => import("./pages/owner-dashboard/OwnerWorkoutPrograms.jsx"));
const OwnerEngagement = React.lazy(() => import("./pages/owner-dashboard/OwnerEngagement.jsx"));
const OwnerRetail = React.lazy(() => import("./pages/owner-dashboard/OwnerRetail.jsx"));
const OwnerOnDemand = React.lazy(() => import("./pages/owner-dashboard/OwnerOnDemand.jsx"));
const OwnerGroups = React.lazy(() => import("./pages/owner-dashboard/OwnerGroups.jsx"));
const OwnerBranding = React.lazy(() => import("./pages/owner-dashboard/OwnerBranding.jsx"));
const OwnerMemberStories = React.lazy(() => import("./pages/owner-dashboard/OwnerMemberStories.jsx"));
const OwnerLive = React.lazy(() => import("./pages/owner-dashboard/OwnerLive.jsx"));
const OwnerTV = React.lazy(() => import("./pages/owner-dashboard/OwnerTV.jsx"));
const OwnerPerformance = React.lazy(() => import("./pages/owner-dashboard/OwnerPerformance.jsx"));
const OwnerLeaderboard = React.lazy(() => import("./pages/owner-dashboard/OwnerLeaderboard.jsx"));
const OwnerLoyalty = React.lazy(() => import("./pages/owner-dashboard/OwnerLoyalty.jsx"));
const OwnerFeedback = React.lazy(() => import("./pages/owner-dashboard/OwnerFeedback.jsx"));
// Expenses & Payouts / Events / Support — three complete backends (saasOwnerExpenses
// + saasOwnerPayouts, saasOwnerEvents, saasOwnerTickets) that had no owner-portal page.
const OwnerExpenses = React.lazy(() => import("./pages/owner-dashboard/OwnerExpenses.jsx"));
const OwnerEvents = React.lazy(() => import("./pages/owner-dashboard/OwnerEvents.jsx"));
const OwnerSupport = React.lazy(() => import("./pages/owner-dashboard/OwnerSupport.jsx"));
const OwnerPartialPayments = React.lazy(() => import("./pages/owner-dashboard/OwnerPartialPayments.jsx"));
const OwnerUpgradePtMembership = React.lazy(() => import("./pages/owner-dashboard/OwnerUpgradePtMembership.jsx"));
const OwnerTransfer = React.lazy(() => import("./pages/owner-dashboard/OwnerTransfer.jsx"));
const OwnerPtAnalytics = React.lazy(() => import("./pages/owner-dashboard/OwnerPtAnalytics.jsx"));
const OwnerReports = React.lazy(() => import("./pages/owner-dashboard/OwnerReports.jsx"));
const OwnerAskAi = React.lazy(() => import("./pages/owner-dashboard/OwnerAskAi.jsx"));
const OwnerWhatsNewPage = React.lazy(() => import("./pages/owner-dashboard/OwnerWhatsNewPage.jsx"));
const OwnerMonthlyGoal = React.lazy(() => import("./pages/owner-dashboard/OwnerMonthlyGoal.jsx"));
const OwnerInvoices = React.lazy(() => import("./pages/owner-dashboard/OwnerInvoices.jsx"));
const OwnerTaxSettings = React.lazy(() => import("./pages/owner-dashboard/OwnerTaxSettings.jsx"));
const OwnerGymHours = React.lazy(() => import("./pages/owner-dashboard/OwnerGymHours.jsx"));
const OwnerTrainers = React.lazy(() => import("./pages/owner-dashboard/OwnerTrainers.jsx"));
const OwnerImportMembers = React.lazy(() => import("./pages/owner-dashboard/OwnerImportMembers.jsx"));
const OwnerCashRestriction = React.lazy(() => import("./pages/owner-dashboard/OwnerCashRestriction.jsx"));
const OwnerWaiverSettings = React.lazy(() => import("./pages/owner-dashboard/OwnerWaiverSettings.jsx"));
const MemberConsentForm = React.lazy(() => import("./pages/MemberConsentForm.jsx"));
const GymLandingPublic = React.lazy(() => import("./pages/GymLandingPublic.jsx"));
// WGO-F090 — per-gym subdomain tenancy. `gymSlugFromHost` is exhaustively
// tested and FAILS CLOSED (null on the apex, www, app/api/…, localhost, preview
// hosts and anything unrecognised), so a null slug means today's behaviour.
const GymHostBranding = React.lazy(() => import("./components/tenant/GymHostBranding"));
const OwnerDailyTarget = React.lazy(() => import("./pages/owner-dashboard/OwnerDailyTarget.jsx"));
const OwnerPtTrials = React.lazy(() => import("./pages/owner-dashboard/OwnerPtTrials.jsx"));
const OwnerLeadForm = React.lazy(() => import("./pages/owner-dashboard/OwnerLeadForm.jsx"));
const LeadCapturePage = React.lazy(() => import("./pages/owner-dashboard/LeadCapturePage.jsx"));
const OwnerSubscriptionForm = React.lazy(() => import("./pages/owner-dashboard/OwnerSubscriptionForm.jsx"));
const OwnerSettings = React.lazy(() => import("./pages/owner-dashboard/OwnerSettings.jsx"));
const OwnerSetProfile = React.lazy(() => import("./pages/owner-dashboard/OwnerSetProfile.jsx"));
const OwnerSetAccount = React.lazy(() => import("./pages/owner-dashboard/OwnerSetAccount.jsx"));
const OwnerSetSecurity = React.lazy(() => import("./pages/owner-dashboard/OwnerSetSecurity.jsx"));
const OwnerSetNotifications = React.lazy(() => import("./pages/owner-dashboard/OwnerSetNotifications.jsx"));
const OwnerSetBilling = React.lazy(() => import("./pages/owner-dashboard/OwnerSetBilling.jsx"));
const OwnerSetProviders = React.lazy(() => import("./pages/owner-dashboard/OwnerSetProviders.jsx"));
const OwnerSetActivity = React.lazy(() => import("./pages/owner-dashboard/OwnerSetActivity.jsx"));
const OwnerSetHelp = React.lazy(() => import("./pages/owner-dashboard/OwnerSetHelp.jsx"));
const SaasPlans = React.lazy(() => import("./pages/saas-plans/SaasPlans.jsx"));
const SaasOverview = React.lazy(() => import("./pages/saas-overview/SaasOverview.jsx"));
const WhatsNewAdminGate = React.lazy(() => import("./pages/whats-new-admin/WhatsNewAdminGate.jsx"));
const DemoManagement = React.lazy(() => import("./pages/demo-management/DemoManagement.jsx"));
const SaasAdminLayout = React.lazy(() => import("./pages/saas-admin/SaasAdminLayout.jsx"));
const SaasOwners = React.lazy(() => import("./pages/saas-admin/SaasOwners.jsx"));
const SaasSubscriptions = React.lazy(() => import("./pages/saas-admin/SaasSubscriptions.jsx"));
const SaasMonetization = React.lazy(() => import("./pages/saas-admin/SaasMonetization.jsx"));
const SaasGyms = React.lazy(() => import("./pages/saas-admin/SaasGyms.jsx"));
const SaasTeam = React.lazy(() => import("./pages/saas-admin/SaasTeam.jsx"));
const SaasAdminAccept = React.lazy(() => import("./pages/saas-admin/SaasAdminAccept.jsx"));
const SaasRoles = React.lazy(() => import("./pages/saas-admin/SaasRoles.jsx"));
const SaasNotifications = React.lazy(() => import("./pages/saas-admin/SaasNotifications.jsx"));
const SaasActivity = React.lazy(() => import("./pages/saas-admin/SaasActivity.jsx"));
const LeadList = React.lazy(() => import("./pages/saas-admin/leads/LeadList.jsx"));
const LeadDetail = React.lazy(() => import("./pages/saas-admin/leads/LeadDetail.jsx"));
const LeadBoard = React.lazy(() => import("./pages/saas-admin/leads/LeadBoard.jsx"));
const LeadSettings = React.lazy(() => import("./pages/saas-admin/leads/LeadSettings.jsx"));
const LeadImport = React.lazy(() => import("./pages/saas-admin/leads/LeadImport.jsx"));
// not LeadReports: that name belongs to the old /lms-report page (commented out below)
const AdminLeadReports = React.lazy(() => import("./pages/saas-admin/leads/LeadReports.jsx"));
const SaasOnboarding = React.lazy(() => import("./pages/saas-admin/SaasOnboarding.jsx"));
// Owner Payments: Bills, Cash Approvals, Transactions and Owner Billing as four tabs of one page
const SaasOwnerPayments = React.lazy(() => import("./pages/saas-admin/SaasOwnerPayments.jsx"));
const PaymentsRedirect = React.lazy(() => import("./pages/saas-admin/SaasOwnerPayments.jsx").then((m) => ({ default: m.PaymentsRedirect })));
const SaasServiceAddons = React.lazy(() => import("./pages/saas-admin/SaasServiceAddons.jsx"));
const AdminNotFound = React.lazy(() => import("./pages/saas-admin/AdminNotFound.jsx"));
const WhatsNewAdmin = React.lazy(() => import("./pages/whats-new-admin/WhatsNewAdmin.jsx"));
const BookDemoPage = React.lazy(() => import("./pages/book-demo/BookDemoPage.jsx"));
// the gym TV — pairs with a code, no login (TV Workouts, 2026-10-22)
const TvScreen = React.lazy(() => import("./pages/tv/TvScreen.jsx"));
const DemoTokenPage = React.lazy(() => import("./pages/book-demo/DemoTokenPage.jsx"));
const PaymentLinkReturn = React.lazy(() => import("./pages/payment-link/PaymentLinkReturn.jsx"));
const SupportEnter = React.lazy(() => import("./pages/support/SupportEnter.jsx"));
const StaffLeaveManagement = React.lazy(() => import("./pages/open-pages/StaffLeaveManagement.jsx"));
const LeaveManagement = React.lazy(() => import("./pages/leave-management/LeaveManagement.jsx"));
const LiveMonitoring = React.lazy(() => import("./pages/live-monitoring/LiveMonitoring.jsx"));
const ApprovalManager = React.lazy(() => import("./pages/open-pages/ApprovalManager.jsx"));
const LeadManagement = React.lazy(() => import("./pages/lead-management/LeadManagement.jsx"));
const LeadAnalyticsGymwise = React.lazy(() => import("./pages/lead-management/LeadAnalyticsGymwise.jsx"));
const GymVisitAnalytics = React.lazy(() => import("./pages/analytics/GymVisitAnalytics.jsx"));
const FollowupAnalyticsPage = React.lazy(() => import("./pages/lead-management/FollowupAnalytics.jsx"));
const LeadManagementOpen = React.lazy(() => import("./pages/open-pages/LeadManagementOpen.jsx"));
const FreshLeads = React.lazy(() => import("./pages/lead-management/FreshLeads.jsx"));
const PreSaleGymLeads = React.lazy(() => import("./pages/lead-management/PreSaleGymLeads.jsx"));
const AgentMapping = React.lazy(() => import("./pages/campaign/AgentMapping.jsx"));
const FitnessProfile = React.lazy(() => import("./pages/diet/FitnessProfile.jsx"));
const FollowupStatuses = React.lazy(() => import("./pages/lead-management/FollowupStatuses.jsx"));
const CustomerIntent = React.lazy(() => import("./pages/lead-management/CustomerIntent.jsx"));
const URE = React.lazy(() => import("./pages/unpaid-renewal-expiring/URE.jsx"));
const PMLinkOpenPage = React.lazy(() => import("./pages/open-pages/PMLinkOpenPage.jsx"));
const WorkoutsExercise = React.lazy(() => import("./pages/workouts-new/WorkoutsExercise.jsx"));
const WorkoutEquipments = React.lazy(() => import("./pages/workouts-new/WorkoutEquipments.jsx"));
const WorkoutPackages = React.lazy(() => import("./pages/workouts-new/WorkoutPackages.jsx"));
const Testimonial = React.lazy(() => import("./pages/website-content/Testimonial.jsx"));
const WhatsNew = React.lazy(() => import("./pages/website-content/WhatsNew.jsx"));
const WorkoutGoals = React.lazy(() => import("./pages/website-content/WorkoutGoals.jsx"));
const UREOpen = React.lazy(() => import("./pages/unpaid-renewal-expiring/UREOpen.jsx"));
const IncentiveCalculation = React.lazy(() => import("./pages/incentive-systems/IncentiveCalculation.jsx"));
const WorkoutCategories = React.lazy(() => import("./pages/workouts-new/WorkoutCategories.jsx"));
const RawLead = React.lazy(() => import("./pages/lead-management/RawLead.jsx"));
const AllTasks = React.lazy(() => import("./pages/hygiene-system/AllTasks.jsx"));
const HygieneCategories = React.lazy(() => import("./pages/hygiene-system/HygieneCategories.jsx"));
const HygieneManagement = React.lazy(() => import("./pages/hygiene-system/HygieneManagement.jsx"));
const GymPlans = React.lazy(() => import("./pages/payment-flow/GymPlans.jsx"));
const CuponRequests = React.lazy(() => import("./pages/payment-flow/CuponRequests.jsx"));
const Partners = React.lazy(() => import("./pages/website-content/Partners.jsx"));
const FranchiseLeads = React.lazy(() => import("./pages/website-content/FranchiseLeads.jsx"));
const CuponRequestOpen = React.lazy(() => import("./pages/open-pages/CuponRequestOpen.jsx"));
const CartHistory = React.lazy(() => import("./pages/payment-link/CartHistory.jsx"));
const Users = React.lazy(() => import("./pages/users/Users.jsx"));
const OnboardingCenter = React.lazy(() => import("./pages-v2/onboarding/OnboardingCenter.jsx"));
const ProfilePage = React.lazy(() => import("./pages-v2/profile-menu/ProfilePage.jsx"));
const AnalyticsPage = React.lazy(() => import("./pages-v2/profile-menu/AnalyticsPage.jsx"));
const SettingsPage = React.lazy(() => import("./pages-v2/profile-menu/SettingsPage.jsx"));
const PrivacyPage = React.lazy(() => import("./pages-v2/profile-menu/PrivacyPage.jsx"));
const HelpCenterPage = React.lazy(() => import("./pages-v2/profile-menu/HelpCenterPage.jsx"));
const HomePage = React.lazy(() => import("./pages-v2/home/HomePage.jsx"));
const NotFound = React.lazy(() => import("./pages/NotFound.jsx"));
// Offline is deliberately NOT lazy — see its static import at the top of the file.
const UnAuthorized = React.lazy(() => import("./pages/UnAuthorized.jsx"));
const TrainerFeedbacks = React.lazy(() => import("./pages/trainer-feedback/TrainerFeedback.jsx"));
const TrainerReport = React.lazy(() => import("./pages/trainer-report/TrainerReport.jsx"));
const TrainerBCAEvaluation = React.lazy(() => import("./pages/trainer-report/TrainerBCAEvaluation.jsx"));
const PaymentLink = React.lazy(() => import("./pages/payment-link/PaymentLink.jsx"));
const PartialPayment = React.lazy(() => import("./pages/payment-link/PartialPayment"));
const Subscriptions = React.lazy(() => import("./pages/reports-subscription/Subscriptions"));
const PTTrialTrack = React.lazy(() => import("./pages/open-pages/PTTrialTrack"));
const DietMeals = React.lazy(() => import("./pages/diet/DietMeals"));
const Ingredients = React.lazy(() => import("./pages/diet/Ingredients"));
const DietTimings = React.lazy(() => import("./pages/diet/DietTimings"));
const DietUnit = React.lazy(() => import("./pages/diet/DietUnit.jsx"));
const DietType = React.lazy(() => import("./pages/diet/DietType"));
const Dieticians = React.lazy(() => import("./pages/diet/Dieticians"));
const DietPackage = React.lazy(() => import("./pages/diet/DietPackage.jsx"));
const DietDashboard = React.lazy(() => import("./pages/diet/DietDashboard"));
const DietPlan = React.lazy(() => import("./pages/diet/DietPlan"));
const SalesExportPage = React.lazy(() => import("./pages/payment-link/SalesExportPage.jsx"));
const MemberPage = React.lazy(() => import("./pages/member/MemberPage.jsx"));
const SalesPage = React.lazy(() => import("./pages/sales/SalesPage.jsx"));
const ResetPassword = React.lazy(() => import("./pages/onboarding/ResetPassword.jsx"));
const PTTrackOpenForm = React.lazy(() => import("./pages/open-pages/PTTrackOpenForm"));
const CommonOpenPage = React.lazy(() => import("./pages/open-pages/CommonOpenPage.jsx"));
const CampaignSystem = React.lazy(() => import("./pages/automation/CampaignSystem.jsx"));
const JobSystem = React.lazy(() => import("./pages/automation/JobSystem.jsx"));
const VoiceoverSystem = React.lazy(() => import("./pages/automation/VoiceoverSystem.jsx"));
const CTOStats = React.lazy(() => import("./pages/cto/CTOStats.jsx"));
const TrainerMemberEngagement = React.lazy(() => import("./pages/open-pages/TrainerMemberEngagement.jsx"));
const TMELogs = React.lazy(() => import("./pages/trainer-member-engagement/TMELogs.jsx"));
const Career = React.lazy(() => import("./pages/website-content/Career.jsx"));
const Tv = React.lazy(() => import("./pages/tv-section/tv.jsx"));
const AttendanceReport = React.lazy(() => import("./pages/staff-report/AttendanceReport.jsx"));
const AgreementReport = React.lazy(() => import("./pages/agreement-report/agreementReport.jsx"));
const LeadClosureReport = React.lazy(() => import("./pages/lead-closure-report/LeadClosureReport.jsx"));
const OneDayMembership = React.lazy(() => import("./pages/subscription/OneDayMembership.jsx"));
const OneDayMembershipOpen = React.lazy(() => import("./pages/open-pages/OneDayMembershipOpen.jsx"));
const MetaIntegration = React.lazy(() => import("./pages/meta-integration/MetaIntegration.jsx"));
const MetaSettings = React.lazy(() => import("./pages/meta-settings/MetaSettings.jsx"));
const OfferMapping = React.lazy(() => import("./pages/offer-mapping/OfferMapping.jsx"));
const AllGymOffers = React.lazy(() => import("./pages/offer-mapping/AllGymOffers.jsx"));
const OfferAdding = React.lazy(() => import("./pages/offer-mapping/OfferAdding.jsx"));
const TvMonitoringDashboard = React.lazy(() => import("./pages/tv-monitoring/TvMonitoringDashboard.jsx"));
const WtfBanner = React.lazy(() => import("./pages/wtf-banners/WtfBanner.jsx"));
const PTProspectManagement = React.lazy(() => import("./pages/pt-closure/PTProspectManagement.jsx"));
const PTProspectManagementOpen = React.lazy(() => import("./pages/open-pages/PTProspectManagementOpen.jsx"));
const DefferedPayments = React.lazy(() => import("./pages/payment-flow/DefferedPayments.jsx"));
const DefferedPaymentsOpen = React.lazy(() => import("./pages/open-pages/DefferedPaymentsOpen.jsx"));
const ExpenseManagement = React.lazy(() => import("./pages/expense-management/ExpenseManagement.jsx"));
const ExpenseCategories = React.lazy(() => import("./pages/expense-management/ExpenseCategories.jsx"));
const ExpenseProcessManagers = React.lazy(() => import("./pages/expense-management/ExpenseProcessManagers.jsx"));
const ExpenseManagementOpen = React.lazy(() => import("./pages/open-pages/ExpenseManagementOpen.jsx"));
const Refferals = React.lazy(() => import("./pages/refferal/Refferals.jsx"));
const FitnessCenter = React.lazy(() => import("./pages/fitness-center/FitnessCenter.jsx"));
const AdBlogs = React.lazy(() => import("./pages/ad-blogs/AdBlogs.jsx"));
const BlogCategories = React.lazy(() => import("./pages/ad-blogs/BlogCategories.jsx"));
const DashboardFeatures = React.lazy(() => import("./pages/feature-control/DashboardFeatures.jsx"));
const DashboardMenus = React.lazy(() => import("./pages/feature-control/DashboardMenus.jsx"));
const PageMap = React.lazy(() => import("./pages/feature-control/PageMap.jsx"));
const ImportMembersOpen = React.lazy(() => import("./pages/open-pages/ImportMembersOpen.jsx"));
const ImportMembersAdmin = React.lazy(() => import("./pages/import-members/ImportMembersAdmin.jsx"));
const TrainerIncentivesOpen = React.lazy(() => import("./pages/open-pages/TrainerIncentivesOpen.jsx"));
const PTTransfer = React.lazy(() => import("./pages/pt-transfer/PTTransfer.jsx"));
const BTLVendor = React.lazy(() => import("./pages/btl-leads/BTLVendor.jsx"));
const UpgradeMembership = React.lazy(() => import("./pages/upgrade-membership/UpgradeMembership.jsx"));
const UpgradeMembershipOpen = React.lazy(() => import("./pages/upgrade-membership/UpgradeMembershipOpen.jsx"));
const UpgradePtMembership = React.lazy(() => import("./pages/upgrade-pt-membership/UpgradePtMembership.jsx"));
const BMR = React.lazy(() => import("./pages/biomax-registered-members/BMR.jsx"));
const TimeSheetOpen = React.lazy(() => import("./pages/time-sheet/TimeSheetOpen.jsx"));
const ActiveMembers = React.lazy(() => import("./pages/active-members/ActiveMembers.jsx"));
const UnpaidMembers = React.lazy(() => import("./pages/unpaid-members/UnpaidMembers.jsx"));
const Checkins = React.lazy(() => import("./pages/checkins/Checkins.jsx"));
const Docs = React.lazy(() => import("./pages/feature-docs/Docs.jsx"));
const FDTargets = React.lazy(() => import("./pages/agents-reports/FDTargets.jsx"));
const DateSaleReport = React.lazy(() => import("./pages/agents-reports/DateSaleReport.jsx"));
const FDTrainersSale = React.lazy(() => import("./pages/agents-reports/FDTrainersSale.jsx"));
const TaskSOP = React.lazy(() => import("./pages/agents-reports/TaskSOP.jsx"));
const DocsOpen = React.lazy(() => import("./pages/feature-docs/DocsOpen.jsx"));
const HygineTaskFrequency = React.lazy(() => import("./pages/hygiene-system/HygineTaskFrequency.jsx"));
const HygineOpenForm = React.lazy(() => import("./pages/hygiene-system/HygineOpenForm.jsx"));
const HygineForm = React.lazy(() => import("./pages/hygiene-system/HygineForm.jsx"));
const HygineSubmittedTasks = React.lazy(() => import("./pages/hygiene-system/HygineSubmittedTasks.jsx"));
const HygineSubmittedTasksOpen = React.lazy(() => import("./pages/hygiene-system/HygineSubmittedTasksOpen.jsx"));
const HygineGymwise = React.lazy(() => import("./pages/hygiene-system/HygineGymwise.jsx"));
const HygineTaskApproval = React.lazy(() => import("./pages/hygiene-system/HygineTaskApproval.jsx"));
const HygineTaskApprovalOpen = React.lazy(() => import("./pages/hygiene-system/HygineTaskApprovalOpen.jsx"));
const HygieneCommandCenter = React.lazy(() => import("./pages/hygiene-system/HygieneCommandCenter.jsx"));
const OnBoardingForm = React.lazy(() => import("./pages/staff-onboarding/OnBoardingForm.jsx"));
const OnboardedStaff = React.lazy(() => import("./pages/staff-onboarding/OnboardedStaff.jsx"));
const HousekeepingOnboard = React.lazy(() => import("./pages/staff-onboarding/HousekeepingOnboard.jsx"));
const DashFeatureGrouping = React.lazy(() => import("./pages/feature-control/DashFeatureGrouping.jsx"));
const Receptionists = React.lazy(() => import("./pages/receptionist-management/Receptionists.jsx"));
const Training = React.lazy(() => import("./pages/trainings/Training.jsx"));
const DFApprovals = React.lazy(() => import("./pages/payment-flow/deffered-approvals/DFApprovals.jsx"));
const RecurringSubscriptions = React.lazy(() => import("./pages/subscription/RecurringSubscriptions.jsx"));
const TrainerPage = React.lazy(() => import("./pages/trainers/TrainerPage.jsx"));
const TrainersRatingPage = React.lazy(() => import("./pages/trainers/TrainersRatingPage.jsx"));
const Version = React.lazy(() => import("./pages/version/Version.jsx"));
const Gallery = React.lazy(() => import("./pages/gallery/index.jsx"));
const WtfBenefits = React.lazy(() => import("./pages/wtf-benefits/WtfBenefits.jsx"));
const Notification = React.lazy(() => import("./pages/notification/Notification.jsx"));
const DiscountPayments = React.lazy(() => import("./pages/payment-flow/discount-payments/DiscountPayments.jsx"));
const DiscountPaymentsOpen = React.lazy(() => import("./pages/payment-flow/discount-payments/DiscountPaymentsOpen.jsx"));
const GymCities = React.lazy(() => import("./pages/gym-cities/GymCities.jsx"));
const RewardSystem = React.lazy(() => import("./pages/reward-system/RewardSystem.jsx"));
const GymFeedback = React.lazy(() => import("./pages/gym-feedback/GymFeedback.jsx"));
const MetabaseLinks = React.lazy(() => import("./pages/metabase/MetabaseLinks.jsx"));
const DynamicLinkin = React.lazy(() => import("./pages/branch-io/DynamicLinkin.jsx"));
const CashVerificationPage = React.lazy(() => import("./pages/cash-verification/CashVerificationPage.jsx"));
const CashReports = React.lazy(() => import("./pages/reports-subscription/CashReports.jsx"));
const GymLandingPages = React.lazy(() => import("./pages/website-content/GymLandingPages.jsx"));
const GymLandingListings = React.lazy(() => import("./pages/website-content/GymLandingListings.jsx"));
const WebsiteTabPreference = React.lazy(() => import("./pages/plans/WebsiteTabPreference.jsx"));
const CentralisedPlans = React.lazy(() => import("./pages/plans/CentralisedPlans.jsx"));
const CentralisedPTPlans = React.lazy(() => import("./pages/plans/CentralisedPTPlans.jsx"));
const PtPlansManage = React.lazy(() => import("./pages/pt-plans/PtPlansManage.jsx"));
const PlansCategories = React.lazy(() => import("./pages/plans/PlansCategories.jsx"));
const AddonsCategories = React.lazy(() => import("./pages/addons/AddonsCategories.jsx"));
const GymTransferSystem = React.lazy(() => import("./pages/transfer-system/GymTransferSystem.jsx"));
const MembershipTransferSystem = React.lazy(() => import("./pages/transfer-system/MembershipTransferSystem.jsx"));
const Transfer = React.lazy(() => import("./pages/transfer-system/Transfer.jsx"));
const AcademyCourses = React.lazy(() => import("./pages/academy/academy-courses.jsx"));
const WtfInvestors = React.lazy(() => import("./pages/wtf-investors/WtfInvestors.jsx"));
const WtfTeam = React.lazy(() => import("./pages/wtf-team/WtfTeam.jsx"));
const PauseSystem = React.lazy(() => import("./pages/pause-system/PauseSystem"));
const PauseMembership = React.lazy(() => import("./pages/pause-system/PauseMembership"));
const HygieneTaskCategories = React.lazy(() => import("./pages/hygiene-system/HygieneTaskCategories.jsx"));
const LateCheckins = React.lazy(() => import("./pages/staff-report/LateCheckins.jsx"));
const GymPlansOpen = React.lazy(() => import("./pages/payment-flow/GymPlansOpen.jsx"));
const Triggers = React.lazy(() => import("./pages/trigger-system/Triggers.jsx"));
const Inventories = React.lazy(() => import("./pages/trigger-system/Inventories.jsx"));
const Templates = React.lazy(() => import("./pages/broadcast-system/Templates.jsx"));
const Variables = React.lazy(() => import("./pages/broadcast-system/Variables.jsx"));
const SendyLists = React.lazy(() => import("./pages/broadcast-system/sendy/SendyLists.jsx"));
const BroadcastTemplate = React.lazy(() => import("./pages/broadcast-system/BroadcastTemplate.jsx"));
const DailyTargetPage = React.lazy(() => import("./pages/home/daily-target/DailyTarget.jsx"));
const FitnessProfileLogs = React.lazy(() => import("./pages/fitness-profile/FitnessProfileLogs.jsx"));
const HealthTracking = React.lazy(() => import("./pages/fitness-profile/HealthTracking.jsx"));
const BCAScheduler = React.lazy(() => import("./pages/broadcast-system/BCAScheduler.jsx"));
const BCAScheduledWatiLogs = React.lazy(() => import("./pages/broadcast-system/BCAScheduledWatiLogs.jsx"));
const LMSBeta = React.lazy(() => import("./pages/lead-management/LMSBeta.jsx"));
const SegmentOfferMapping = React.lazy(() => import("./pages/trigger-system/SegmentOfferMapping.jsx"));
const PayroleProcessor = React.lazy(() => import("./pages/payrole-system/PayroleProcessor.jsx"));
const TicketSystem = React.lazy(() => import("./pages/ticket-system/ticketSystem.jsx"));
const LMSClosureReport = React.lazy(() => import("./pages/lead-closure-report/LMSClosureReport.jsx"));
const ThreeDayPTTrial = React.lazy(() => import("./pages/pt-closure/ThreeDayPTTrial.jsx"));
const StaffProfiles = React.lazy(() => import("./pages/staff-report/StaffProfiles.jsx"));
const AcademyLeads = React.lazy(() => import("./pages/lead-management/AcademyLeads.jsx"));
const GymAlias = React.lazy(() => import("./pages/gym-alias/GymAlias.jsx"));
const SubscriptionExtension = React.lazy(() => import("./pages/subscription-extenstion/SubscriptionExtension.jsx"));
const LiveSubsciptions = React.lazy(() => import("./pages/live-subscriptions/LiveSubsciptions.jsx"));
const LiveBCAPending = React.lazy(() => import("./pages/live-subscriptions/LiveBCAPending.jsx"));
const LivePTTrialPending = React.lazy(() => import("./pages/live-subscriptions/LivePTTrialPending.jsx"));
const LiveMemberCheckins = React.lazy(() => import("./pages/live-subscriptions/LiveMemberCheckins.jsx"));
const LiveMemberReport = React.lazy(() => import("./pages/live-subscriptions/LiveMemberReport.jsx"));
const LiveRenewalReminder = React.lazy(() => import("./pages/live-subscriptions/LiveRenewalReminder.jsx"));
const TrackingSystem = React.lazy(() => import("./pages/tracking-system/TrackingSystem.jsx"));
const FitnessCoachLeads = React.lazy(() => import("./pages/fitness-coach-leads/fitnessCoachLeads.jsx"));
const Regularization = React.lazy(() => import("./pages/staff-report/Regularization.jsx"));
const ESSLMemberDump = React.lazy(() => import("./pages/essl/ESSLMemberDump.jsx"));
const WTF360Consultations = React.lazy(() => import("./pages/wtf-360/WTF360Consultations.jsx"));
const TrainerNotificationLogs = React.lazy(() => import("./pages/trainer-report/TrainerNotificationLogs.jsx"));
const AmplifyLeads = React.lazy(() => import("./pages/wtf-amplify-leads/AmplifyLeads.jsx"));
const FDIncentives = React.lazy(() => import("./pages/incentive-systems/FDIncentives.jsx"));
const CallLogs = React.lazy(() => import("./pages/campaign/CallLogs.jsx"));
const UnifiedPTView = React.lazy(() => import("./pages/unified-pt-view/UnifiedPTView.jsx"));
const PsLegalDoc = React.lazy(() => import("./pages/product-site/pages/LegalDoc.jsx"));
const PsHome = React.lazy(() => import("./pages/product-site/pages/Home.jsx"));
const PsPlatform = React.lazy(() => import("./pages/product-site/pages/Platform.jsx"));
const PsFeatures = React.lazy(() => import("./pages/product-site/pages/Features.jsx"));
const PsFeatureDetail = React.lazy(() => import("./pages/product-site/pages/FeatureDetail.jsx"));
const PsMembershipsBilling = React.lazy(() => import("./pages/product-site/pages/MembershipsBilling.jsx"));
const PsPayments = React.lazy(() => import("./pages/product-site/pages/Payments.jsx"));
const PsCrmLeads = React.lazy(() => import("./pages/product-site/pages/CrmLeads.jsx"));
const PsReportsAnalytics = React.lazy(() => import("./pages/product-site/pages/ReportsAnalytics.jsx"));
const PsRetentionRenewals = React.lazy(() => import("./pages/product-site/pages/RetentionRenewals.jsx"));
const PsPersonalTraining = React.lazy(() => import("./pages/product-site/pages/PersonalTraining.jsx"));
const PsAskYourBusiness = React.lazy(() => import("./pages/product-site/pages/AskYourBusiness.jsx"));
const PsGymWebsites = React.lazy(() => import("./pages/product-site/pages/GymWebsites.jsx"));
const PsWaiversEsign = React.lazy(() => import("./pages/product-site/pages/WaiversEsign.jsx"));
const PsMessaging = React.lazy(() => import("./pages/product-site/pages/Messaging.jsx"));
const PsStaffPermissions = React.lazy(() => import("./pages/product-site/pages/StaffPermissions.jsx"));
const PsAi = React.lazy(() => import("./pages/product-site/pages/Ai.jsx"));
const PsSwitch = React.lazy(() => import("./pages/product-site/pages/Switch.jsx"));
const PsSolutions = React.lazy(() => import("./pages/product-site/pages/Solutions.jsx"));
const PsIndependentGyms = React.lazy(() => import("./pages/product-site/pages/IndependentGyms.jsx"));
const PsBoutiqueStudios = React.lazy(() => import("./pages/product-site/pages/BoutiqueStudios.jsx"));
const PsFunctionalFitness = React.lazy(() => import("./pages/product-site/pages/FunctionalFitness.jsx"));
const PsMultiLocation = React.lazy(() => import("./pages/product-site/pages/MultiLocation.jsx"));
const PsWellnessMedspa = React.lazy(() => import("./pages/product-site/pages/WellnessMedspa.jsx"));
const PsPricing = React.lazy(() => import("./pages/product-site/pages/Pricing.jsx"));
const PsResources = React.lazy(() => import("./pages/product-site/pages/Resources.jsx"));
const PsCompany = React.lazy(() => import("./pages/product-site/pages/Company.jsx"));
const PsLive = React.lazy(() => import("./pages/product-site/pages/Live.jsx"));
// wtfgos.com/gym — every published gym, nearest to the visitor first (each opens /gym/:slug)
const PsGymFinder = React.lazy(() => import("./pages/product-site/pages/GymFinder.jsx"));

// ---------------------------------------------------------------------------
// Product-marketing site (BLUE theme, scope `.ps`) — 25 lazy public routes.
// Foundation pass registers them ONCE; Wave-1 builders only fill page bodies.
// ---------------------------------------------------------------------------

// Redirect legacy /owner-dashboard/* portal links to the new /app/* prefix,
// preserving the sub-path + query so old bookmarks / links keep working.
const LegacyPortalRedirect = () => {
  const loc = useLocation();
  const to = loc.pathname.replace(/^\/owner-dashboard/, "/app") + (loc.search || "");
  return <Navigate to={to} replace />;
};

const App = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Connection loss is handled here, above <Routes>, rather than as a route:
  // it can happen on ANY screen, and a route would need something to navigate
  // to it — which is exactly what fails when the network drops mid-session.
  // `online`/`offline` fire on the OS link state, so this reacts the moment
  // Wi-Fi is switched off and swaps back by itself on reconnect.
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);

  const isAuthenticated = useSelector(selectIsAuth);
  const token = useSelector(selectToken);

  // WGO-F090 — which gym (if any) this HOSTNAME belongs to. Computed once per
  // mount; the hostname cannot change without a full page load. null for the
  // apex, www, app.wtfgos.com, every other reserved host, localhost and preview
  // hosts — and null means the route table below is exactly today's.
  const gymHostSlug = useMemo(() => {
    try {
      // The apex is build-time configurable so local/dev can point at a wildcard
      // DNS domain (e.g. localtest.me, which resolves *anything* to 127.0.0.1).
      // Without that, every gym subdomain needs an /etc/hosts line — sudo-gated,
      // and stale the moment a new gym is created. Unset → production wtfgos.com.
      const apex = process.env.REACT_APP_GYM_APEX;
      return gymSlugFromHost(window.location.hostname, apex ? { apex } : undefined);
    } catch (e) {
      return null; // no window / exotic environment → today's behaviour
    }
  }, []);

  const handleLogout = useCallback(() => {
    // A WTF Go support tab never "logs out" into the owner login screen: its session is
    // simply over, and the portal shows the ended screen (SupportSessionBanner).
    if (isSupportMode()) { markSupportEnded(); return; }
    // Previous logic: If user was on /open-access path, redirect back to /open-access
    // if (pathname.startsWith("/open-access")) {
    //   navigate("/open-access", { state: { from: pathname } });
    // } else {
    //   navigate("/login", { state: { from: pathname } });
    // }

    // Platform staff sign in at /admin, gym owners at /login. Sending an admin
    // whose session ended to the owner screen ("Log in to manage your gyms")
    // looked like the wrong account, and their credentials do not work there.
    //
    // Read the path from the browser, not from the closure: the fetch wrapper
    // below is installed once and would otherwise hold whatever page the app
    // started on.
    const here = (typeof window !== "undefined" && window.location && window.location.pathname) || pathname || "";
    const backTo = here.startsWith("/admin") ? "/admin" : "/login";
    navigate(backTo, { state: { from: pathname } });

    dispatch(logout());
  }, [pathname, navigate, dispatch]);

  // Previous logic: /open-access paths were excluded from authentication check
  // and users could access open-access pages without login.
  // New logic: All /open-access/ paths now redirect to /login
  // const excludedPaths = [
  //   "/open-access",
  //   "/login",
  //   "/subscriptions",
  //   "/enquiry",
  //   "/daily-operation-checklist-form-open",
  //   "/staff-on-board",
  // ];

  useEffect(() => {
    // Redirect /open-access/ paths to /login
    if (pathname.startsWith("/open-access/") || pathname === "/open-access") {
      navigate("/login", { state: { from: pathname } });
      return;
    }

    const excludedPaths = [
      // "/open-access", // Commented out: Previously allowed open-access without login
      "/login",
      "/signup",
      "/saas",   // public SaaS marketing landing page
      // Public product-marketing site (wtfgos.com) — all these are unauthenticated pages.
      "/platform",
      "/features",   // /features + /features/* (memberships-billing, payments, crm-leads, ...)
      "/solutions",  // /solutions + /solutions/* (independent-gyms, boutique-studios, ...)
      "/pricing",
      "/resources",
      "/company",
      "/live",   // public "WTF Go live in the world" map page
      "/ai",
      "/switch",
      // Legal / policy pages (public, US-based) — /legal/:slug + the clean aliases.
      // Were MISSING here, so the global force-login redirect bounced every footer
      // legal link to /login for signed-out visitors. "/legal" covers /legal + /legal/*.
      "/legal", "/terms", "/cookies", "/acceptable-use", "/refund-policy",
      "/sms-terms", "/accessibility", "/dpa", "/security", "/do-not-sell",
      "/gym/",   // public per-gym landing page (/gym/:slug) — trailing slash so it never matches an authed /app/gyms route
      "/cash-verify",
      "/subscriptions",
      "/enquiry",
      "/daily-operation-checklist-form-open",
      "/staff-on-board",
      "/book-demo",
      "/demo/", // public reschedule/cancel (/demo/:token); trailing slash so it does NOT match the authed /demo-management admin route
      "/consent/", // public member consent/health-form signing via emailed token (/consent/:token); trailing slash so it never matches an authed route
      "/whats-new-admin", // self-contained superadmin authoring — has its OWN login gate, so skip the global redirect
      "/admin", // platform admin console — own login gate (SaasAdminRoute), skip the global redirect
      "/support/", // WTF Go support hand-off (/support/enter#t=…) — authenticates itself
      "/payment-link/", // Razorpay sends a signed-out owner here after paying a staff payment link
    ];

    // The site root ("/") is the public marketing landing, and "/gym" the public gym finder —
    // never force login there. EXACT matches: "/gym" as a prefix would also make /gym-plans,
    // /gym-cities, /gym-transfer … public (each gym's own page is "/gym/" above).
    if (pathname !== "/" && pathname !== "/gym" && !excludedPaths.some((path) => pathname.startsWith(path))) {
      if (!isAuthenticated || isTokenExpired(token)) {
        handleLogout();
      }
    }
  }, [isAuthenticated, token, pathname, navigate, handleLogout]);

  // QA DEF-19 — owners were thrown to /login mid-session with a valid token and no word.
  // (1) A 24-hour timer used to log out unconditionally, whatever the token said; the
  //     token's own expiry is already checked on every navigation above, so it is gone.
  // (2) The fetch wrapper logged out on ANY 401 — a third-party call, a permission
  //     refusal, a database hiccup the server reported as 401. It now acts only when our
  //     API says the session is over (`token: "expired"`), and says so on screen.
  // The wrapper is installed once, so it reads handleLogout through a ref (the original
  // captured the first render's pathname for ever).
  const logoutRef = React.useRef(handleLogout);
  logoutRef.current = handleLogout;
  const loggingOutRef = React.useRef(false);
  useEffect(() => { if (token) loggingOutRef.current = false; }, [token]);
  const lastAccessRefreshRef = React.useRef(0);

  useEffect(() => {
    const originalFetch = window.fetch;

    // Only OUR api, and only a call that actually carried the session token.
    // A 401 on a request with no Authorization header means "this call was not
    // authenticated", not "your session died" — treating the two the same threw
    // the admin out of the console whenever any unauthenticated fetch ran.
    const apiBase = process.env.REACT_APP_API_BASEURL || "";
    const isOurApi = (input) => {
      try {
        const url = typeof input === "string" ? input : (input && input.url) || "";
        if (!url) return false;
        if (apiBase && url.startsWith(apiBase)) return true;
        return url.startsWith("/") && !url.startsWith("//");   // same-origin path
      } catch (e) { return false; }
    };
    const hadToken = (input, init) => {
      const pick = (h) => {
        if (!h) return "";
        if (typeof h.get === "function") return h.get("Authorization") || h.get("authorization") || "";
        return h.Authorization || h.authorization || "";
      };
      return Boolean(pick(init && init.headers) || pick(input && input.headers));
    };

    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);

        // A WTF Go support session ended: show the ended screen in a support tab, and do
        // NOTHING elsewhere — the /support/enter hand-off still shares the admin's storage,
        // so treating it as a logout would sign the admin out.
        if (response.status === 401 && isOurApi(args[0]) && hadToken(args[0], args[1])
            && await isSupportEndedResponse(response)) {
          if (isSupportMode()) markSupportEnded();
          return response;
        }

        // All three: OUR api, a call that carried the session token (admin QA — an
        // unauthenticated fetch's 401 is not "your session died"), AND the server saying
        // the session is over (`token: "expired"`, QA DEF-19 — a permission refusal or a
        // DB hiccup is not a logout either).
        if (response.status === 401 && isOurApi(args[0]) && hadToken(args[0], args[1])
            && await isSessionExpiredResponse(response)) {
          if (!loggingOutRef.current) {
            loggingOutRef.current = true;
            toast.info("Your session has expired — please log in again.", { toastId: "session-expired" });
            logoutRef.current();
          }
        }

        // Refused on ACCESS grounds (FEATURE_READ_ONLY / FEATURE_NOT_GRANTED): the page
        // shows the server's sentence; the portal re-reads my-features so the nav and the
        // buttons match the person's role again (the owner may have just changed it).
        // At most once every 3s — a page that fires several calls refreshes once.
        if (response.status === 403 && isOurApi(args[0]) && hadToken(args[0], args[1])
            && await isAccessRefusalResponse(response)) {
          const now = Date.now();
          if (now - lastAccessRefreshRef.current > 3000) {
            lastAccessRefreshRef.current = now;
            window.dispatchEvent(new Event("ownp:access-changed"));
          }
        }

        return response;
      } catch (error) {
        console.error("Fetch error:", error);
        throw error;
      }
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  if (isOffline) return <Offline />;

  return (
    <CurrencyProvider>
      {/* Outside <Suspense> on purpose: the banner must be visible while a route
          chunk is still downloading, which is exactly when a first-time visitor
          from an ad is sitting on the page. */}
      <ConsentManager />
      {/* The NEUTRAL variant. This boundary wraps EVERY route — marketing site,
          owner portal, admin console — so it cannot assume any one layout. The
          dashboard-shaped variant exists for page-level use where the shape is
          actually known; using it here put stat tiles and table rows on the
          marketing home page, promising a layout that never arrives. */}
      <Suspense fallback={<PageSkeleton variant="plain" />}>
        <Routes>
        {/* -------------------------------------------------------------------
            WGO-F090 — PER-GYM SUBDOMAIN TENANCY. Only mounted when the hostname
            IS a gym front door (`<gym>.wtfgos.com`); `gymHostSlug` is null on
            the apex, www, app/api/admin/…, localhost and preview hosts, and
            these two <Route>s then do not exist at all — React.Children skips
            the `false`, so the route table is byte-for-byte today's.
            Same paths as the marketing "/" and "/login" below; declared FIRST so
            they win the tie on a gym host only.
              /       → that gym's public landing (the SAME GymLandingPublic the
                        apex serves at /gym/:slug — one implementation)
              /login  → the SAME owner/staff login, wrapped in gym branding
            ------------------------------------------------------------------- */}
        {gymHostSlug && (
          <Route path="/" element={<GymHostLanding slug={gymHostSlug} />} />
        )}
        {gymHostSlug && (
          <Route
            path="/login"
            element={
              <GymHostBranding slug={gymHostSlug}>
                <OwnerSignupWizard />
              </GymHostBranding>
            }
          />
        )}
        {/* -------------------------------------------------------------------
            Public product-marketing site (BLUE theme, scope `.ps`). 25 lazy
            routes above the app shell. Home replaces the old SaasLanding at "/".
            ------------------------------------------------------------------- */}
        <Route element={<SuspenseWrapper />}>
          <Route path="/" element={<PsHome />} />
          <Route path="/platform" element={<PsPlatform />} />
          <Route path="/features" element={<PsFeatures />} />
          <Route path="/features/memberships-billing" element={<PsMembershipsBilling />} />
          <Route path="/features/payments" element={<PsPayments />} />
          <Route path="/features/crm-leads" element={<PsCrmLeads />} />
          <Route path="/features/reports-analytics" element={<PsReportsAnalytics />} />
          <Route path="/features/retention-renewals" element={<PsRetentionRenewals />} />
          <Route path="/features/personal-training" element={<PsPersonalTraining />} />
          <Route path="/features/ask-your-business" element={<PsAskYourBusiness />} />
          <Route path="/features/gym-websites" element={<PsGymWebsites />} />
          <Route path="/features/waivers-esign" element={<PsWaiversEsign />} />
          <Route path="/features/messaging" element={<PsMessaging />} />
          <Route path="/features/staff-permissions" element={<PsStaffPermissions />} />
          <Route path="/features/:slug" element={<PsFeatureDetail />} />
          <Route path="/ai" element={<PsAi />} />
          <Route path="/switch" element={<PsSwitch />} />
          <Route path="/solutions" element={<PsSolutions />} />
          <Route path="/solutions/independent-gyms" element={<PsIndependentGyms />} />
          <Route path="/solutions/boutique-studios" element={<PsBoutiqueStudios />} />
          <Route path="/solutions/functional-fitness" element={<PsFunctionalFitness />} />
          <Route path="/solutions/multi-location" element={<PsMultiLocation />} />
          <Route path="/solutions/wellness-medspa" element={<PsWellnessMedspa />} />
          <Route path="/pricing" element={<PsPricing />} />
          <Route path="/resources" element={<PsResources />} />
          <Route path="/company" element={<PsCompany />} />
          <Route path="/live" element={<PsLive />} />
          <Route path="/gym" element={<PsGymFinder />} />
          {/* Legal / policy pages (US-based). Data-driven: /legal/:slug + clean aliases. */}
          <Route path="/legal/:slug" element={<PsLegalDoc />} />
          <Route path="/legal" element={<PsLegalDoc slug="privacy" />} />
          <Route path="/terms" element={<PsLegalDoc slug="terms" />} />
          <Route path="/cookies" element={<PsLegalDoc slug="cookies" />} />
          <Route path="/acceptable-use" element={<PsLegalDoc slug="acceptable-use" />} />
          <Route path="/refund-policy" element={<PsLegalDoc slug="refund" />} />
          <Route path="/sms-terms" element={<PsLegalDoc slug="sms" />} />
          <Route path="/accessibility" element={<PsLegalDoc slug="accessibility" />} />
          <Route path="/dpa" element={<PsLegalDoc slug="dpa" />} />
          <Route path="/security" element={<PsLegalDoc slug="security" />} />
          <Route path="/do-not-sell" element={<PsLegalDoc slug="do-not-sell" />} />
        </Route>

        <Route path={"/"} element={<Layout />}>

          <Route element={<PrivateRoute path="/home" />}>
            <Route path="/home" element={<Home />} />
          </Route>

          <Route element={<PrivateRoute path="/onboarding" />}>
            <Route path="/onboarding" element={<OnboardingCenter />} />
          </Route>

          {/* Profile Menu Pages - accessible to all logged-in users */}
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/help-center" element={<HelpCenterPage />} />

        <Route element={<PrivateRoute path="/dashboard-menus" />}>
          <Route path="/dashboard-menus" element={<DashboardMenus />} />
        </Route>

        <Route element={<PrivateRoute path="/dashboard-features" />}>
          <Route path="/dashboard-features" element={<DashboardFeatures />} />
        </Route>

        <Route element={<PrivateRoute path="/dashboard-feature-map" />}>
          <Route path="/dashboard-feature-map" element={<PageMap />} />
        </Route>

        <Route element={<PrivateRoute path="/pt-flow" />}>
          <Route path="/pt-flow" element={<PersonalTraining />} />
        </Route>

        <Route element={<PrivateRoute path="/trainers-rating" />}>
          <Route path="/trainers-rating" element={<TrainersRatingPage />} />
        </Route>

        <Route element={<PrivateRoute path="/pt-flow-beta" />}>
          <Route path="/pt-flow-beta" element={<PTFlow />} />
        </Route>

        <Route element={<PrivateRoute path="/unified-pt-view" />}>
          <Route path="/unified-pt-view" element={<UnifiedPTView />} />
        </Route>

        <Route element={<PrivateRoute path="/incentive-system" />}>
          <Route path="/incentive-system" element={<IncentiveSystem />} />
        </Route>

        <Route element={<PrivateRoute path="/trainer-incentives" />}>
          <Route
            path="/trainer-incentives"
            element={<IncentiveCalculation />}
          />
        </Route>

        <Route element={<PrivateRoute path="/payment-link" />}>
          <Route path="/payment-link" element={<PaymentLink />} />
        </Route>

        <Route element={<PrivateRoute path="/partial-payments" />}>
          <Route path="/partial-payments" element={<PartialPayment />} />
        </Route>

        <Route element={<PrivateRoute path="/trainer-feedback" />}>
          <Route path="/trainer-feedback" element={<TrainerFeedbacks />} />
        </Route>

        <Route element={<PrivateRoute path="/users" />}>
          <Route path="/users" element={<Users />} />
        </Route>

        <Route element={<PrivateRoute path="/biomax-registered-members" />}>
          <Route path="/biomax-registered-members" element={<BMR />} />
        </Route>

        <Route element={<PrivateRoute path="/active-members" />}>
          <Route path="/active-members" element={<ActiveMembers />} />
        </Route>

        <Route element={<PrivateRoute path="/unpaid-members" />}>
          <Route path="/unpaid-members" element={<UnpaidMembers />} />
        </Route>

        <Route element={<PrivateRoute path="/checkins" />}>
          <Route path="/checkins" element={<Checkins />} />
        </Route>

        <Route element={<PrivateRoute path="/member" />}>
          <Route path="/member" element={<MemberPage />} />
        </Route>

        <Route element={<PrivateRoute path="/subscription" />}>
          <Route path="/subscription" element={<Subscriptions />} />
        </Route>

        <Route element={<PrivateRoute path="/one-day-membership" />}>
          <Route path="/one-day-membership" element={<OneDayMembership />} />
        </Route>

        <Route element={<PrivateRoute path="/meta-integration" />}>
          <Route path="/meta-integration" element={<MetaIntegration />} />
        </Route>

        <Route element={<PrivateRoute path="/meta-settings" />}>
          <Route path="/meta-settings" element={<MetaSettings />} />
        </Route>

        <Route element={<PrivateRoute path="/sales-data" />}>
          <Route path="/sales-data" element={<SalesExportPage />} />
        </Route>

        <Route element={<PrivateRoute path="/trainer-report" />}>
          <Route path="/trainer-report" element={<TrainerReport />} />
        </Route>

        <Route element={<PrivateRoute path="/trainer-bca-evaluation" />}>
          <Route
            path="/trainer-bca-evaluation"
            element={<TrainerBCAEvaluation />}
          />
        </Route>

        <Route element={<PrivateRoute path="/agreement-report" />}>
          <Route path="/agreement-report" element={<AgreementReport />} />
        </Route>

        <Route element={<PrivateRoute path="/daily-target" />}>
          <Route path="/daily-target" element={<DailyTargetPage />} />
        </Route>

        <Route element={<PrivateRoute path="/sales/today" />}>
          <Route path="/sales/today" element={<SalesPage />} />
        </Route>

        <Route element={<PrivateRoute path="/pt-track" />}>
          <Route path="/pt-track" element={<PTTrialTrack />} />
        </Route>

        <Route element={<PrivateRoute path="/live-monitoring" />}>
          <Route path="/live-monitoring" element={<LiveMonitoring />} />
        </Route>

        {/* <Route element={<PrivateRoute path="/live-checkin" />}>
          <Route path="/live-checkin" element={<LiveCheckin />} />
        </Route> */}

        <Route element={<PrivateRoute path="/customer-intents" />}>
          <Route path="/customer-intents" element={<CustomerIntent />} />
        </Route>

        <Route element={<PrivateRoute path="/followup-status" />}>
          <Route path="/followup-status" element={<FollowupStatuses />} />
        </Route>

        <Route
          element={<PrivateRoute path="/renewal-expiring-unpaid-members" />}
        >
          <Route path="/renewal-expiring-unpaid-members" element={<URE />} />
        </Route>

        <Route element={<PrivateRoute path="/pt-transfer" />}>
          <Route path="/pt-transfer" element={<PTTransfer />} />
        </Route>

        {/* WORKOUTS */}
        <Route element={<PrivateRoute path="/equipments" />}>
          <Route path="/equipments" element={<WorkoutEquipments />} />
        </Route>

        <Route element={<PrivateRoute path="/workout-goals" />}>
          <Route path="/workout-goals" element={<WorkoutGoals />} />
        </Route>

        <Route element={<PrivateRoute path="/workout-categories" />}>
          <Route path="/workout-categories" element={<WorkoutCategories />} />
        </Route>

        <Route element={<PrivateRoute path="/exercise" />}>
          <Route path="/exercise" element={<WorkoutsExercise />} />
        </Route>

        <Route element={<PrivateRoute path="/workout-packages" />}>
          <Route path="/workout-packages" element={<WorkoutPackages />} />
        </Route>
        {/* --------------- WORKOUTS ------------- */}

        <Route element={<PrivateRoute path="/gym-plans" />}>
          <Route path="/gym-plans" element={<GymPlans />} />
        </Route>

        <Route element={<PrivateRoute path="/membership-requests" />}>
          <Route path="/membership-requests" element={<CuponRequests />} />
        </Route>

        <Route element={<PrivateRoute path="/deffered-payments" />}>
          <Route path="/deffered-payments" element={<DefferedPayments />} />
        </Route>


        <Route element={<PrivateRoute path="/discount-payments" />}>
          <Route path="/discount-payments" element={<DiscountPayments />} />
          <Route
            path="/discount-payments/:uid"
            element={<DiscountPayments />}
          />
        </Route>

        <Route element={<PrivateRoute path="/membership-cart-history" />}>
          <Route path="/membership-cart-history" element={<CartHistory />} />
        </Route>

        <Route element={<PrivateRoute path="/trainer-member-engagement" />}>
          <Route path="/trainer-member-engagement" element={<TMELogs />} />
        </Route>

        <Route element={<PrivateRoute path="/pt-prospects" />}>
          <Route path="/pt-prospects" element={<PTProspectManagement />} />
        </Route>

        <Route element={<PrivateRoute path="/expense-categories" />}>
          <Route path="/expense-categories" element={<ExpenseCategories />} />
        </Route>

        <Route element={<PrivateRoute path="/expense-process-managers" />}>
          <Route
            path="/expense-process-managers"
            element={<ExpenseProcessManagers />}
          />
        </Route>

        <Route element={<PrivateRoute path="/expense-management" />}>
          <Route path="/expense-management" element={<ExpenseManagement />} />
        </Route>

        <Route element={<PrivateRoute path="/referral" />}>
          <Route path="/referral" element={<Refferals />} />
        </Route>

        <Route element={<PrivateRoute path="/import-members" />}>
          <Route path="/import-members" element={<ImportMembersAdmin />} />
        </Route>

        <Route element={<PrivateRoute path="/attendance-report" />}>
          <Route path="/attendance-report" element={<AttendanceReport />} />
        </Route>

        <Route element={<PrivateRoute path="/late-checkins" />}>
          <Route path="/late-checkins" element={<LateCheckins />} />
        </Route>

        <Route element={<PrivateRoute path="/payouts" />}>
          <Route path="/payouts" element={<WTFPayouts />} />
        </Route>

        <Route element={<PrivateRoute path="/settlement-system" />}>
          <Route path="/settlement-system" element={<SettlementSystem />} />
        </Route>

        <Route element={<PrivateRoute path="/centralized-plan" />}>
          <Route path="/centralized-plan" element={<Plans />} />
        </Route>

        <Route element={<PrivateRoute path="/centralized-plan-beta" />}>
          <Route path="/centralized-plan-beta" element={<CentralisedPlans />} />
        </Route>

        <Route element={<PrivateRoute path="/centralized-pt-plan-beta" />}>
          <Route path="/centralized-pt-plan-beta" element={<CentralisedPTPlans />} />
        </Route>

        <Route element={<PrivateRoute path="/pt-plans" />}>
          <Route path="/pt-plans" element={<PtPlansManage />} />
        </Route>

        <Route element={<PrivateRoute path="/cto" />}>
          <Route path="/cto" element={<CTOStats />} />
        </Route>

        <Route element={<PrivateRoute path="/keyword-actions" />}>
          <Route path="/keyword-actions" element={<KeywordActions />} />
        </Route>

        <Route element={<PrivateRoute path="/agent-mapping" />}>
          <Route path="/agent-mapping" element={<AgentMapping />} />
        </Route>

        <Route element={<PrivateRoute path="/campaign-logs" />}>
          <Route path="/campaign-logs" element={<CampaignSystem />} />
        </Route>

        <Route element={<PrivateRoute path="/campaign-jobs" />}>
          <Route path="/campaign-jobs" element={<JobSystem />} />
        </Route>

        <Route element={<PrivateRoute path="/voice-campaign" />}>
          <Route path="/voice-campaign" element={<VoiceoverSystem />}></Route>

          <Route element={<PrivateRoute path="/compose_voice_camping" />}>
            <Route path="/compose_voice_camping" element={<ComposeVC />} />
          </Route>
        </Route>

        <Route element={<PrivateRoute path="/leave-management" />}>
          <Route path="/leave-management" element={<LeaveManagement />} />
        </Route>

        <Route element={<PrivateRoute path="/hygiene-categories" />}>
          <Route path="/hygiene-categories" element={<HygieneCategories />} />
        </Route>

        <Route element={<PrivateRoute path="/hygiene-tasks" />}>
          <Route path="/hygiene-tasks" element={<AllTasks />} />
        </Route>

        <Route element={<PrivateRoute path="/hygiene-manage" />}>
          <Route path="/hygiene-manage" element={<HygieneManagement />} />
        </Route>

        <Route element={<PrivateRoute path="/fitness-center" />}>
          <Route path="/fitness-center" element={<FitnessCenter />} />
        </Route>

        <Route element={<PrivateRoute path="/fitness-profile-logs" />}>
          <Route
            path="/fitness-profile-logs"
            element={<FitnessProfileLogs />}
          />
        </Route>

        <Route element={<PrivateRoute path="/health-tracking" />}>
          <Route path="/health-tracking" element={<HealthTracking />} />
        </Route>

        {/* WEBSITE CONTENT */}
        <Route element={<PrivateRoute path="/testimonial" />}>
          <Route path="/testimonial" element={<Testimonial />} />
        </Route>

        <Route element={<PrivateRoute path="/partners" />}>
          <Route path="/partners" element={<Partners />} />
        </Route>

        <Route element={<PrivateRoute path="/franchise-leads" />}>
          <Route path="/franchise-leads" element={<FranchiseLeads />} />
        </Route>

        <Route element={<PrivateRoute path="/career" />}>
          <Route path="/career" element={<Career />} />
        </Route>

        <Route element={<PrivateRoute path="/offer-mapping" />}>
          <Route path="/offer-mapping" element={<OfferMapping />} />
        </Route>

        <Route element={<PrivateRoute path="/all-gym-offers" />}>
          <Route path="/all-gym-offers" element={<AllGymOffers />} />
        </Route>

        <Route element={<PrivateRoute path="/add-offers" />}>
          <Route path="/add-offers" element={<OfferAdding />} />
        </Route>

        <Route element={<PrivateRoute path="/tv-monitoring" />}>
          <Route path="/tv-monitoring" element={<TvMonitoringDashboard />} />
        </Route>

        <Route element={<PrivateRoute path="/wtf-banners" />}>
          <Route path="/wtf-banners" element={<WtfBanner />} />
        </Route>

        <Route element={<PrivateRoute path="/saas-plans" />}>
          <Route path="/saas-plans" element={<SaasPlans />} />
        </Route>

        <Route element={<PrivateRoute path="/saas-overview" />}>
          <Route path="/saas-overview" element={<SaasOverview />} />
        </Route>

        <Route element={<PrivateRoute path="/demo-management" />}>
          <Route path="/demo-management" element={<DemoManagement />} />
        </Route>

        <Route element={<PrivateRoute path="/blog-categories" />}>
          <Route path="/blog-categories" element={<BlogCategories />} />
        </Route>

        <Route element={<PrivateRoute path="/blogs" />}>
          <Route path="/blogs" element={<AdBlogs />} />
        </Route>
        {/* ---------------- WEBSITE CONTENT ---------------- */}

        {/* TV  */}
        <Route element={<PrivateRoute path="/tv" />}>
          <Route path="/tv" element={<Tv />} />
        </Route>

        <Route element={<PrivateRoute path="/tv-notifications" />}>
          <Route path="/tv-notifications" element={<TvNotification />} />
        </Route>
        {/* --------------TV-------------  */}

        {/* DIET */}
        <Route element={<PrivateRoute path="/diet-meal" />}>
          <Route path="/diet-meal" element={<DietMeals />} />
        </Route>

        <Route element={<PrivateRoute path="/ingredients" />}>
          <Route path="/ingredients" element={<Ingredients />} />
        </Route>

        <Route element={<PrivateRoute path="/diet-unit" />}>
          <Route path="/diet-unit" element={<DietUnit />} />
        </Route>

        <Route element={<PrivateRoute path="/diet-timing" />}>
          <Route path="/diet-timing" element={<DietTimings />} />
        </Route>

        <Route element={<PrivateRoute path="/diet-type" />}>
          <Route path="/diet-type" element={<DietType />} />
        </Route>

        <Route element={<PrivateRoute path="/diet-package" />}>
          <Route path="/diet-package" element={<DietPackage />} />
        </Route>

        <Route element={<PrivateRoute path="/fitness-profile" />}>
          <Route path="/fitness-profile" element={<FitnessProfile />} />
        </Route>

        <Route element={<PrivateRoute path="/dietician" />}>
          <Route path="/dietician" element={<Dieticians />} />
        </Route>

        <Route element={<PrivateRoute path="/diet-plan" />}>
          <Route path="/diet-plan" element={<DietPlan />} />
        </Route>

        <Route element={<PrivateRoute path="/diet-dashboard" />}>
          <Route path="/diet-dashboard" element={<DietDashboard />} />
        </Route>

        <Route element={<PrivateRoute path="/btl-vendor" />}>
          <Route path="/btl-vendor" element={<BTLVendor />} />
        </Route>
        {/* ------------- DIET ----------------- */}

        <Route element={<PrivateRoute path="/raw-lead" />}>
          <Route path="/raw-lead" element={<RawLead />} />
        </Route>

        <Route element={<PrivateRoute path="/lms-beta" />}>
          <Route path="/lms-beta" element={<LMSBeta />} />
        </Route>

        {/* <Route element={<PrivateRoute path="/lms-report" />}>
          <Route path="/lms-report" element={<LeadReports />} />
        </Route> */}

        <Route element={<PrivateRoute path="/lms-closure-report" />}>
          <Route path="/lms-closure-report" element={<LMSClosureReport />} />
        </Route>

        <Route element={<PrivateRoute path="/lead-management" />}>
          <Route path="/lead-management" element={<LeadManagement />} />
        </Route>

        <Route element={<PrivateRoute path="/lead-analytics-gymwise" />}>
          <Route path="/lead-analytics-gymwise" element={<LeadAnalyticsGymwise />} />
        </Route>

        <Route element={<PrivateRoute path="/gym-visit-analytics" />}>
          <Route path="/gym-visit-analytics" element={<GymVisitAnalytics />} />
        </Route>

        <Route element={<PrivateRoute path="/whats-new" />}>
          <Route path="/whats-new" element={<WhatsNew />} />
        </Route>

        <Route element={<PrivateRoute path="/lead-management/followup-analytics" />}>
          <Route path="/lead-management/followup-analytics" element={<FollowupAnalyticsPage />} />
        </Route>

        <Route element={<PrivateRoute path="/fresh-lead" />}>
          <Route path="/fresh-lead" element={<FreshLeads />} />
        </Route>

        <Route element={<PrivateRoute path="/pre-sale-gym-leads" />}>
          <Route path="/pre-sale-gym-leads" element={<PreSaleGymLeads />} />
        </Route>

        <Route element={<PrivateRoute path="/lead-closure-report" />}>
          <Route path="/lead-closure-report" element={<LeadClosureReport />} />
        </Route>

        <Route element={<PrivateRoute path="/upgrade-membership" />}>
          <Route path="/upgrade-membership" element={<UpgradeMembership />} />
        </Route>

        <Route element={<PrivateRoute path="/upgrade-pt-membership" />}>
          <Route path="/upgrade-pt-membership" element={<UpgradePtMembership />} />
        </Route>

        {/* <Route element={<PrivateRoute path="/time-sheet" />}>
          <Route path="/time-sheet" element={<TimeSheet />} />
        </Route> */}

        <Route element={<PrivateRoute path="/dashboard-docs" />}>
          <Route path="/dashboard-docs" element={<Docs />} />
          <Route path="/dashboard-docs/:uid" element={<Docs />} />
        </Route>

        <Route element={<PrivateRoute path="/fd-targets" />}>
          <Route path="/fd-targets" element={<FDTargets />} />
        </Route>

        <Route element={<PrivateRoute path="/daily-sales" />}>
          <Route path="/daily-sales" element={<DateSaleReport />} />
        </Route>

        <Route element={<PrivateRoute path="/fd-trainer-sales" />}>
          <Route path="/fd-trainer-sales" element={<FDTrainersSale />} />
        </Route>

        <Route element={<PrivateRoute path="/task-sop" />}>
          <Route path="/task-sop" element={<TaskSOP />} />
        </Route>

        <Route
          element={<PrivateRoute path="/daily-operation-checklist-status" />}
        >
          <Route
            path="/daily-operation-checklist-status"
            element={<HygieneCategories />}
          />
        </Route>

        <Route element={<PrivateRoute path="/hygine-task-frequency" />}>
          <Route
            path="/hygine-task-frequency"
            element={<HygineTaskFrequency />}
          />
        </Route>

        <Route
          element={<PrivateRoute path="/daily-operation-task-categories" />}
        >
          <Route
            path="/daily-operation-task-categories"
            element={<HygieneTaskCategories />}
          />
        </Route>

        <Route
          element={<PrivateRoute path="/daily-operation-checklist-tasks" />}
        >
          <Route
            path="/daily-operation-checklist-tasks"
            element={<AllTasks />}
          />
        </Route>

        <Route
          element={<PrivateRoute path="/daily-operation-checklist-form" />}
        >
          <Route
            path="/daily-operation-checklist-form"
            element={<HygineForm />}
          />
        </Route>

        <Route element={<PrivateRoute path="/daily-operation-checklist" />}>
          <Route
            path="/daily-operation-checklist"
            element={<HygineSubmittedTasks />}
          />
        </Route>

        <Route
          element={<PrivateRoute path="/daily-operation-checklist-gymwise" />}
        >
          <Route
            path="/daily-operation-checklist-gymwise"
            element={<HygineGymwise />}
          />
        </Route>

        <Route
          element={<PrivateRoute path="/daily-operation-checklist-approval" />}
        >
          <Route
            path="/daily-operation-checklist-approval"
            element={<HygineTaskApproval />}
          />
        </Route>

        <Route element={<PrivateRoute path="/hygiene-command-center" />}>
          <Route
            path="/hygiene-command-center"
            element={<HygieneCommandCenter />}
          />
        </Route>

        <Route element={<PrivateRoute path="/trainers" />}>
          <Route path="/trainers" element={<TrainerPage />} />
        </Route>

        <Route element={<PrivateRoute path="/version" />}>
          <Route path="/version" element={<Version />} />
        </Route>

        <Route element={<PrivateRoute path="/staff-onboarding-requests" />}>
          <Route
            path="/staff-onboarding-requests"
            element={<OnboardedStaff />}
          />
        </Route>

        <Route element={<PrivateRoute path="/housekeeping-on-board" />}>
          <Route
            path="/housekeeping-on-board"
            element={<HousekeepingOnboard />}
          />
        </Route>

        <Route element={<PrivateRoute path="/gallery" />}>
          <Route path="/gallery" element={<Gallery />} />
        </Route>

        <Route element={<PrivateRoute path="/wtf-benefits" />}>
          <Route path="/wtf-benefits" element={<WtfBenefits />} />
        </Route>

        <Route element={<PrivateRoute path="/feature-grouping" />}>
          <Route path="/feature-grouping" element={<DashFeatureGrouping />} />
        </Route>

        <Route element={<PrivateRoute path="/notification" />}>
          <Route path="/notification" element={<Notification />} />
        </Route>

        <Route element={<PrivateRoute path="/receptionists" />}>
          <Route path="/receptionists" element={<Receptionists />} />
        </Route>

        <Route element={<PrivateRoute path="/gym-cities" />}>
          <Route path="/gym-cities" element={<GymCities />} />
        </Route>

        <Route element={<PrivateRoute path="/training" />}>
          <Route path="/training" element={<Training />} />
        </Route>

        <Route element={<PrivateRoute path="/couple-membership-request" />}>
          <Route path="/couple-membership-request" element={<DFApprovals />} />
        </Route>

        <Route element={<PrivateRoute path="/recurring-subscriptions" />}>
          <Route
            path="/recurring-subscriptions"
            element={<RecurringSubscriptions />}
          />
        </Route>

        <Route element={<PrivateRoute path="/reward-system" />}>
          <Route path="/reward-system" element={<RewardSystem />} />
        </Route>

        <Route element={<PrivateRoute path="/feedbacks" />}>
          <Route path="/feedbacks" element={<GymFeedback />} />
        </Route>

        <Route element={<PrivateRoute path="/metabase-links" />}>
          <Route path="/metabase-links" element={<MetabaseLinks />} />
        </Route>

        <Route element={<PrivateRoute path="/dynamic-linking" />}>
          <Route path="/dynamic-linking" element={<DynamicLinkin />} />
        </Route>

        <Route element={<PrivateRoute path="/cash-reports" />}>
          <Route path="/cash-reports" element={<CashReports />} />
          <Route path="/cash-reports/:uid" element={<CashReports />} />
        </Route>

        <Route element={<PrivateRoute path="/gym-landing-pages" />}>
          <Route path="/gym-landing-pages" element={<GymLandingListings />} />
        </Route>

        <Route element={<PrivateRoute path="/gym-landing-details-pages" />}>
          <Route
            path="/gym-landing-details-pages"
            element={<GymLandingPages />}
          />
        </Route>

        <Route element={<PrivateRoute path="/website-tab-preferences" />}>
          <Route
            path="/website-tab-preferences"
            element={<WebsiteTabPreference />}
          />
        </Route>

        <Route element={<PrivateRoute path="/gym-categories" />}>
          <Route path="/gym-categories" element={<PlansCategories />} />
        </Route>

        <Route element={<PrivateRoute path="/addons-categories" />}>
          <Route path="/addons-categories" element={<AddonsCategories />} />
        </Route>

        <Route element={<PrivateRoute path="/transfer" />}>
          <Route path="/transfer" element={<Transfer />} />
        </Route>

        <Route element={<PrivateRoute path="/pause-system" />}>
          <Route path="/pause-system" element={<PauseSystem />} />
        </Route>

        <Route element={<PrivateRoute path="/pause-membership" />}>
          <Route path="/pause-membership" element={<PauseMembership />} />
        </Route>

        <Route element={<PrivateRoute path="/centralised-plan-categories" />}>
          <Route
            path="/centralised-plan-categories"
            element={<PlansCategories />}
          />
        </Route>

        <Route element={<PrivateRoute path="/academy-course-detail" />}>
          <Route path="/academy-course-detail" element={<AcademyCourses />} />
        </Route>

        <Route element={<PrivateRoute path="/academy-course-schedule" />}>
          <Route
            path="/academy-course-schedule"
            element={<AcademyCourseSchedule />}
          />
        </Route>

        <Route element={<PrivateRoute path="/wtf-investors" />}>
          <Route path="/wtf-investors" element={<WtfInvestors />} />
        </Route>

        <Route element={<PrivateRoute path="/wtf-team" />}>
          <Route path="/wtf-team" element={<WtfTeam />} />
        </Route>

        <Route element={<PrivateRoute path="/gym-transfer" />}>
          <Route path="/gym-transfer" element={<GymTransferSystem />} />
        </Route>

        <Route element={<PrivateRoute path="/membership-transfer" />}>
          <Route
            path="/membership-transfer"
            element={<MembershipTransferSystem />}
          />
        </Route>

        <Route element={<PrivateRoute path="/academy-course-detail" />}>
          <Route path="/academy-course-detail" element={<AcademyCourses />} />
        </Route>

        <Route element={<PrivateRoute path="/academy-course-schedule" />}>
          <Route
            path="/academy-course-schedule"
            element={<AcademyCourseSchedule />}
          />
        </Route>

        <Route element={<PrivateRoute path="/wtf-investors" />}>
          <Route path="/wtf-investors" element={<WtfInvestors />} />
        </Route>

        <Route element={<PrivateRoute path="/wtf-team" />}>
          <Route path="/wtf-team" element={<WtfTeam />} />
        </Route>

        <Route element={<PrivateRoute path="/broadcast-templates" />}>
          <Route path="/broadcast-templates" element={<Templates />} />
        </Route>

        <Route element={<PrivateRoute path="/broadcast-variables" />}>
          <Route path="/broadcast-variables" element={<Variables />} />
        </Route>

        <Route element={<PrivateRoute path="/sendy-campaign-lists" />}>
          <Route path="/sendy-campaign-lists" element={<SendyLists />} />
        </Route>

        <Route element={<PrivateRoute path="/broadcast-template" />}>
          <Route path="/broadcast-template" element={<BroadcastTemplate />} />
        </Route>

        <Route element={<PrivateRoute path="/broadcast-scheduler" />}>
          <Route path="/broadcast-scheduler" element={<BCAScheduler />} />
        </Route>

        <Route element={<PrivateRoute path="/scheduled-broadcast-logs" />}>
          <Route
            path="/scheduled-broadcast-logs"
            element={<BCAScheduledWatiLogs />}
          />
        </Route>

        {/* Email Broadcast System */}










        {/* <Route element={<PrivateRoute path="/roaster-manager" />}>
          <Route path="/roaster-manager" element={<RoasterManager />} />
        </Route> */}

        <Route element={<PrivateRoute path="/payroll-processor" />}>
          <Route path="/payroll-processor" element={<PayroleProcessor />} />
        </Route>

        <Route element={<PrivateRoute path="/triggers" />}>
          <Route path="/triggers" element={<Triggers />} />
        </Route>

        <Route element={<PrivateRoute path="/inventories" />}>
          <Route path="/inventories" element={<Inventories />} />
        </Route>

        <Route element={<PrivateRoute path="/segments" />}>
          <Route path="/segments" element={<SegmentOfferMapping />} />
        </Route>

        <Route element={<PrivateRoute path="/payrole-processor" />}>
          <Route path="/payrole-processor" element={<PayroleProcessor />} />
        </Route>
        <Route element={<PrivateRoute path="/ticket-system" />}>
          <Route path="/ticket-system" element={<TicketSystem />} />
        </Route>

        <Route element={<PrivateRoute path="/ticket-user-roles" />}>
          <Route path="/ticket-user-roles" element={<TicketUserRoles />} />
        </Route>

        <Route element={<PrivateRoute path="/ticket-category-management" />}>
          <Route
            path="/ticket-category-management"
            element={<TicketSystemManagement />}
          />
        </Route>

        <Route element={<PrivateRoute path="/sla-rules" />}>
          <Route path="/sla-rules" element={<SLARules />} />
        </Route>

        <Route element={<PrivateRoute path="/three-day-pt-trial" />}>
          <Route path="/three-day-pt-trial" element={<ThreeDayPTTrial />} />
        </Route>

        <Route element={<PrivateRoute path="/staff-profiles" />}>
          <Route path="/staff-profiles" element={<StaffProfiles />} />
        </Route>

        <Route element={<PrivateRoute path="/academy-leads" />}>
          <Route path="/academy-leads" element={<AcademyLeads />} />
        </Route>

        <Route element={<PrivateRoute path="/gym-alias" />}>
          <Route path="/gym-alias" element={<GymAlias />} />
        </Route>

        <Route element={<PrivateRoute path="/subscription-extension" />}>
          <Route
            path="/subscription-extension"
            element={<SubscriptionExtension />}
          />
        </Route>

        <Route element={<PrivateRoute path="/live-subscriptions" />}>
          <Route path="/live-subscriptions" element={<LiveSubsciptions />} />
        </Route>

        <Route element={<PrivateRoute path="/live-bca-pending-logs" />}>
          <Route path="/live-bca-pending-logs" element={<LiveBCAPending />} />
        </Route>

        <Route element={<PrivateRoute path="/live-pt-trial-logs" />}>
          <Route path="/live-pt-trial-logs" element={<LivePTTrialPending />} />
        </Route>

        <Route element={<PrivateRoute path="/live-checkins" />}>
          <Route path="/live-checkins" element={<LiveMemberCheckins />} />
        </Route>

        <Route element={<PrivateRoute path="/live-renewal-reminder" />}>
          <Route
            path="/live-renewal-reminder"
            element={<LiveRenewalReminder />}
          />
        </Route>

        <Route element={<PrivateRoute path="/live-member-report" />}>
          <Route path="/live-member-report" element={<LiveMemberReport />} />
        </Route>

        <Route element={<PrivateRoute path="/tracking-system" />}>
          <Route path="/tracking-system" element={<TrackingSystem />} />
        </Route>

        <Route element={<PrivateRoute path="/fitness-coach-leads" />}>
          <Route path="/fitness-coach-leads" element={<FitnessCoachLeads />} />
        </Route>

        <Route element={<PrivateRoute path="/amplify-leads" />}>
          <Route path="/amplify-leads" element={<AmplifyLeads />} />
        </Route>

        <Route element={<PrivateRoute path="/regularization" />}>
          <Route path="/regularization" element={<Regularization />} />
        </Route>

        <Route element={<PrivateRoute path="/essl-member-dump" />}>
          <Route path="/essl-member-dump" element={<ESSLMemberDump />} />
        </Route>

        <Route element={<PrivateRoute path="/wtf-360-consultations" />}>
          <Route
            path="/wtf-360-consultations"
            element={<WTF360Consultations />}
          />
        </Route>

        <Route element={<PrivateRoute path="/trainer-notification-logs" />}>
          <Route
            path="/trainer-notification-logs"
            element={<TrainerNotificationLogs />}
          />
        </Route>

        <Route element={<PrivateRoute path="/chat-history" />}>
          <Route path="/chat-history" element={<ChatHistory />} />
        </Route>

        <Route element={<PrivateRoute path="/fd-incentives" />}>
          <Route path="/fd-incentives" element={<FDIncentives />} />
        </Route>

        <Route element={<PrivateRoute path="/lead-call-logs" />}>
          <Route path="/lead-call-logs" element={<CallLogs />} />
        </Route>
      </Route>

      <Route element={<SuspenseWrapper />}>
        <Route path="/cash-verify/:token" element={<CashVerificationPage />} />
        <Route path="/saas" element={<PsHome />} />
        <Route path="/login" element={<OwnerSignupWizard />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/legacy-login" element={<Login />} />
        {/* Self-contained superadmin authoring — own login, no legacy dashboard dependency. */}
        <Route path="/whats-new-admin" element={<WhatsNewAdminGate />} />
        <Route path="/signup" element={<OwnerSignupWizard />} />
        <Route path="/lead/:gymToken" element={<LeadCapturePage />} />
        <Route path="/book-demo" element={<BookDemoPage />} />
        <Route path="/tv" element={<TvScreen />} />
        <Route path="/demo/:token" element={<DemoTokenPage />} />
        {/* Razorpay's callback_url for staff payment links (Assign plan / Owner Onboarding) */}
        <Route path="/payment-link/return" element={<PaymentLinkReturn />} />
        {/* WTF Go support hand-off: admin console → owner's dashboard in a new tab */}
        <Route path="/support/enter" element={<SupportEnter />} />
        <Route path="/consent/:token" element={<MemberConsentForm />} />
        <Route path="/gym/:slug" element={<GymLandingPublic />} />
        <Route path="/app" element={<OwnerRoute><OwnerPortalLayout /></OwnerRoute>}>
          <Route index element={<OwnerHome />} />
          <Route path="ask-ai" element={<OwnerAskAi />} />
          <Route path="whats-new" element={<OwnerWhatsNewPage />} />
          <Route path="monthly-goal" element={<OwnerMonthlyGoal />} />
          <Route path="gyms" element={<ManageGyms />} />
          {/* /new routes open the modal popup over the list (not a full-page form) */}
          <Route path="gyms/new" element={<Navigate to="/app/gyms?new=1" replace />} />
          <Route path="gyms/:uid/edit" element={<AddGymWizard />} />
          <Route path="plans" element={<ManagePlans />} />
          <Route path="plans/new" element={<Navigate to="/app/plans?new=1" replace />} />
          <Route path="plans/:uid/edit" element={<OwnerPlanForm />} />
          <Route path="pt-plans" element={<OwnerPtPlans />} />
          <Route path="pt-plans/new" element={<Navigate to="/app/pt-plans?new=1" replace />} />
          <Route path="pt-plans/:uid/edit" element={<OwnerPtPlanForm />} />
          <Route path="staff" element={<OwnerStaff />} />
          <Route path="staff/new" element={<Navigate to="/app/staff?new=1" replace />} />
          <Route path="staff/:uid/edit" element={<OwnerStaffForm />} />
          <Route path="staff/:uid/review" element={<OwnerStaffForm mode="review" />} />
          <Route path="staff/:uid/offboard" element={<OwnerStaffOffboard />} />
          <Route path="roles" element={<OwnerRolesPermissions />} />
          <Route path="permissions" element={<Navigate to="/app/roles" replace />} />
          <Route path="feature-map" element={<Navigate to="/app/roles?tab=team" replace />} />
          <Route path="permission-matrix" element={<Navigate to="/app/roles?tab=compare" replace />} />
          <Route path="members" element={<OwnerMembers />} />
          <Route path="members/new" element={<OwnerMemberForm />} />
          <Route path="members/:uid/edit" element={<OwnerMemberForm />} />
          <Route path="leads" element={<OwnerLeads />} />
          <Route path="leads/new" element={<OwnerLeadForm />} />
          <Route path="leads/:uid/edit" element={<OwnerLeadForm />} />
          <Route path="subscriptions" element={<OwnerSubscriptions />} />
          <Route path="subscriptions/new" element={<OwnerSubscriptionForm />} />
          <Route path="renewals" element={<OwnerRenewalsHub />} />
          {/* Gym Plans catalog merged into Regular/PT Plans (owner edit · staff read-only) */}
          <Route path="gym-plans" element={<Navigate to="/app/plans" replace />} />
          <Route path="partial-payments" element={<OwnerPartialPayments />} />
          <Route path="upgrade-membership" element={<OwnerRenewalsHub initialTab="upgrade" />} />
          <Route path="upgrade-pt-membership" element={<OwnerUpgradePtMembership />} />
          {/* Transfer a member / a membership between your gyms, or to someone else (524) */}
          <Route path="transfer" element={<OwnerTransfer />} />
          <Route path="pt-analytics" element={<OwnerPtAnalytics />} />
          <Route path="reports" element={<OwnerReports />} />
          <Route path="invoices" element={<OwnerInvoices />} />
          <Route path="tax-settings" element={<OwnerTaxSettings />} />
          <Route path="gym-hours" element={<OwnerGymHours />} />
          <Route path="trainers" element={<OwnerTrainers />} />
          <Route path="members-import" element={<OwnerImportMembers />} />
          <Route path="cash-restriction" element={<OwnerCashRestriction />} />
          <Route path="waiver" element={<OwnerWaiverSettings />} />
          {/* Re-enabled on owner request, reversing the 07-13 removal. The
              backing controllers were never removed — only these four routes and
              their nav entries were, so the pages have been dark while their
              APIs kept working. */}
          <Route path="attendance" element={<OwnerAttendance />} />
          <Route path="leave" element={<OwnerLeave />} />
          <Route path="incentives" element={<OwnerIncentive />} />
          <Route path="payroll" element={<OwnerPayroll />} />
          <Route path="daily-target" element={<OwnerDailyTarget />} />
          <Route path="habits" element={<OwnerHabits />} />
          <Route path="progress-photos" element={<OwnerProgressPhotos />} />
          <Route path="wallet" element={<OwnerWallet />} />
          <Route path="rep-max" element={<OwnerRepMax />} />
          {/* Shift Scheduling & Time-Clock (Feature 03). A NEW surface — the four
              routes commented out above stay that way; this is not a revival of
              them. Roster + clock-out live here; clock-IN is still the existing
              attendance check-in. */}
          <Route path="shifts" element={<OwnerShifts />} />
          <Route path="challenges" element={<OwnerChallenges />} />
          <Route path="automations" element={<OwnerAutomations />} />
          <Route path="coupons" element={<OwnerCoupons />} />
          <Route path="classes" element={<OwnerClasses />} />
          <Route path="timetable" element={<OwnerTimetable />} />
          <Route path="campaigns" element={<OwnerCampaigns />} />
          <Route path="audiences" element={<OwnerSegments />} />
          <Route path="email-templates" element={<OwnerTemplates />} />
          <Route path="nutrition" element={<OwnerNutrition />} />
          <Route path="assessments" element={<OwnerAssessments />} />
          <Route path="workout-programs" element={<OwnerWorkoutPrograms />} />
          {/* Trainer Commissions was removed (user 2026-10-30): trainer pay on sales is
              Incentives, which feeds payroll. Old links land there. */}
          <Route path="trainer-commissions" element={<Navigate to="/app/incentives" replace />} />
          <Route path="engagement" element={<OwnerEngagement />} />
          <Route path="retail" element={<OwnerRetail />} />
          <Route path="on-demand" element={<OwnerOnDemand />} />
          <Route path="groups" element={<OwnerGroups />} />
          <Route path="branding" element={<OwnerBranding />} />
          <Route path="member-stories" element={<OwnerMemberStories />} />
          <Route path="live" element={<OwnerLive />} />
          <Route path="tv" element={<OwnerTV />} />
          <Route path="performance" element={<OwnerPerformance />} />
          <Route path="leaderboard" element={<OwnerLeaderboard />} />
          <Route path="loyalty" element={<OwnerLoyalty />} />
          <Route path="feedback" element={<OwnerFeedback />} />
          <Route path="expenses" element={<OwnerExpenses />} />
          <Route path="events" element={<OwnerEvents />} />
          <Route path="support" element={<OwnerSupport />} />
          <Route path="pt-trials" element={<OwnerPtTrials />} />
          {/* Account / Settings area — profile menu (top-right) links here. */}
          <Route path="settings" element={<OwnerSettings />}>
            <Route index element={<Navigate to="/app/settings/profile" replace />} />
            <Route path="profile" element={<OwnerSetProfile />} />
            <Route path="account" element={<OwnerSetAccount />} />
            <Route path="billing" element={<OwnerSetBilling />} />
            {/* <Route path="payouts" element={<OwnerSetPayouts />} /> */}   {/* removed from SaaS (07-13) */}
            <Route path="notifications" element={<OwnerSetNotifications />} />
            <Route path="providers" element={<OwnerSetProviders />} />
            <Route path="security" element={<OwnerSetSecurity />} />
            <Route path="activity" element={<OwnerSetActivity />} />
            <Route path="help/*" element={<OwnerSetHelp />} />
          </Route>
        </Route>

        {/* Platform Admin Console — own login gate + lightweight shell (no legacy dashboard RBAC). */}
        {/* PUBLIC — an invitee has no session yet, by definition. Declared BEFORE
            the guarded /admin block so it can never be swallowed by it; the token
            in the URL is the only credential this page needs (single-use,
            server-side expiry, only its hash is stored). */}
        <Route path="/admin/accept/:token" element={<SaasAdminAccept />} />
        <Route path="/admin" element={<SaasAdminRoute><SaasAdminLayout /></SaasAdminRoute>}>
          <Route index element={<Navigate to="/admin/overview" replace />} />
          {/* signing in at /admin/login (the guard shows the form on any /admin URL) then
              landed on "This admin page does not exist" (QA 2026-11-01) */}
          <Route path="login" element={<Navigate to="/admin/overview" replace />} />
          <Route path="overview" element={<SaasOverview />} />
          <Route path="owners" element={<SaasOwners />} />
          <Route path="subscriptions" element={<SaasSubscriptions />} />
          <Route path="gyms" element={<SaasGyms />} />
          <Route path="plans" element={<SaasPlans />} />
          <Route path="pricing" element={<SaasMonetization />} />
          {/* Member Invoices (gyms' invoices to their members) left the WTF admin (520): an old link → WTF's own invoices */}
          <Route path="invoices" element={<PaymentsRedirect tab="invoices" />} />
          <Route path="transactions" element={<PaymentsRedirect tab="received" />} />
          {/* Lead Management — prospects before they buy (a won lead converts into Owner Onboarding). */}
          <Route path="leads" element={<LeadList />} />
          <Route path="leads/settings" element={<LeadSettings />} />
          <Route path="leads/pipeline" element={<LeadBoard />} />
          <Route path="leads/import" element={<LeadImport />} />
          <Route path="leads/reports" element={<AdminLeadReports />} />
          <Route path="leads/:uid" element={<LeadDetail />} />
          <Route path="onboarding" element={<SaasOnboarding />} />
          {/* one page, four tabs; the old addresses open their tab (query kept) */}
          <Route path="payments" element={<SaasOwnerPayments />} />
          <Route path="payments/:tab" element={<SaasOwnerPayments />} />
          <Route path="owner-invoices" element={<PaymentsRedirect tab="invoices" />} />
          <Route path="bills" element={<PaymentsRedirect tab="collect" />} />
          <Route path="cash-approvals" element={<PaymentsRedirect tab="approvals" />} />
          <Route path="addon-services" element={<SaasServiceAddons />} />
          <Route path="notifications" element={<SaasNotifications />} />
          <Route path="audit-logs" element={<SaasActivity />} />
          <Route path="demo-management" element={<DemoManagement />} />
          <Route path="whats-new" element={<WhatsNewAdmin />} />
          {/* Must sit ABOVE the catch-all, or /admin/team silently renders the
              "not built yet" placeholder instead of the page. */}
          <Route path="team" element={<SaasTeam />} />
          <Route path="roles" element={<SaasRoles />} />
          {/* An unknown admin path is a 404, NOT a roadmap promise (WGO-F067).
              Genuine not-yet-built modules get an EXPLICIT route above this one
              rendering <ModulePlaceholder />. */}
          <Route path="*" element={<AdminNotFound />} />
        </Route>
        {/* Backward-compat: old /owner-dashboard/* links → new /app/* (preserves subpath). */}
        <Route path="/owner-dashboard/*" element={<LegacyPortalRedirect />} />
        <Route path="/owner-dashboard" element={<Navigate to="/app" replace />} />
        <Route path="/unauthorized" element={<UnAuthorized />} />
        <Route path="/subscriptions" element={<SubscriptionDetails />} />
        <Route path="/enquiry" element={<Navigate to="/app/leads" replace />} />
        <Route path="/open-access" element={<CommonOpenPage />} />
        <Route path="/open-access/all-pages" element={<OpenPages />} />
        <Route
          path="/open-access/all-pages/pt-trial-registration"
          element={<PTTrackOpenForm />}
        />
        <Route
          path="/open-access/all-pages/import-members"
          element={<ImportMembersOpen />}
        />
        <Route
          path="/open-access/all-pages/all-leaves"
          element={<StaffLeaveManagement />}
        />
        <Route
          path="/open-access/all-pages/approve-leaves"
          element={<ApprovalManager />}
        />
        <Route
          path="/open-access/all-pages/lead-and-followup-management"
          element={<LeadManagementOpen />}
        />
        <Route
          path="/open-access/all-pages/pending-partial-payments"
          element={<PMLinkOpenPage />}
        />
        <Route
          path="/open-access/all-pages/renewal-expiring-unpaid-members"
          element={<UREOpen />}
        />
        <Route
          path="/open-access/all-pages/membership-requests"
          element={<CuponRequestOpen />}
        />
        <Route
          path="/open-access/all-pages/trainer-member-engagement"
          element={<TrainerMemberEngagement />}
        />
        <Route
          path="/open-access/all-pages/one-day-membership"
          element={<OneDayMembershipOpen />}
        />
        <Route
          path="/open-access/all-pages/pt-prospects"
          element={<PTProspectManagementOpen />}
        />
        <Route
          path="/open-access/all-pages/deffered-payments"
          element={<DefferedPaymentsOpen />}
        />
        <Route
          path="/open-access/all-pages/discount-payments"
          element={<DiscountPaymentsOpen />}
        />
        <Route
          path="/open-access/all-pages/expense-management"
          element={<ExpenseManagementOpen />}
        />
        <Route
          path="/open-access/all-pages/trainer-incentives"
          element={<TrainerIncentivesOpen />}
        />
        <Route
          path="/open-access/all-pages/upgrade-membership"
          element={<UpgradeMembershipOpen />}
        />

        <Route
          path="/open-access/all-pages/time-sheet"
          element={<TimeSheetOpen />}
        />

        <Route
          path="/open-access/all-pages/dashboard-docs"
          element={<DocsOpen />}
        />
        <Route
          path="/open-access/all-pages/dashboard-docs/:uid"
          element={<DocsOpen />}
        />

        <Route
          path="/open-access/all-pages/daily-operation-checklist"
          element={<HygineSubmittedTasksOpen />}
        />

        <Route
          path="/open-access/all-pages/daily-operation-checklist-approval"
          element={<HygineTaskApprovalOpen />}
        />

        <Route
          path="/open-access/all-pages/gym-plans"
          element={<GymPlansOpen />}
        />

        <Route
          path="/daily-operation-checklist-form-open"
          element={<HygineOpenForm />}
        />

        <Route path="/staff-on-board" element={<OnBoardingForm />} />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
      </Suspense>
    </CurrencyProvider>
  );
};

export default App;
